import React, { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal, flushSync } from 'react-dom';
import {
  FaBold,
  FaItalic,
  FaUnderline,
  FaHeading,
  FaLink,
  FaImage,
  FaVideo,
  FaListOl,
  FaListUl,
  FaQuoteRight,
  FaCode,
  FaTable,
  FaMarkdown,
  FaFlag,
  FaChevronLeft,
  FaChevronRight
} from 'react-icons/fa';
import {
  MdSubscript,
  MdSuperscript,
  MdTextIncrease,
  MdTextDecrease,
  MdFormatColorText,
  MdVisibilityOff,
  MdOutline18UpRating,
  MdOpenInFull,
  MdCloseFullscreen,
  MdGridOn,
  MdPsychology
} from 'react-icons/md';
import { GoQuestion } from 'react-icons/go';
import { IoIosMore, IoMdInformationCircleOutline } from 'react-icons/io';
import {
  TbCloudUpload,
  TbCropLandscape,
  TbCropPortrait,
  TbDeviceFloppy,
  TbFileOrientation,
  TbFileTypeDoc,
  TbFileTypeDocx,
  TbFileTypeHtml,
  TbFileTypePdf,
  TbLayoutList,
  TbLayoutRows,
  TbListNumbers,
} from 'react-icons/tb';
import { FiFile, FiFolderPlus, FiLogOut, FiPrinter, FiSave } from 'react-icons/fi';
import { MdOutlineFileUpload, MdOutlineSaveAs } from 'react-icons/md';
import DocumentPageGuides from './DocumentPageGuides';
import { CgToolbarTop } from 'react-icons/cg';
import CenterModal from '../CenterModalWindow/CenterModalWindow';
import Tooltip from '../Tooltip/Tooltip';
import ButtonPill from '../Buttons/button-pills';
import GroupedButton from '../Buttons/GroupedButton';
import CollapseTextButton from '../Buttons/CollapseTextButton';
import NoOutlineTextButton from '../Buttons/noOutline-text';
import ToggleButton from '../Buttons/ToggleButton';
import Dropdown from '../Dropdown/Dropdown';
import '../Dropdown/Dropdown.css';
import TextEditorFileMenu from './TextEditorFileMenu';
import {
  buildLocalDocumentFilePayload,
  parseLocalDocumentFile,
  promptSaveLocalDocumentPickerFirst,
} from './textEditorLocalDocumentFile';
import './TextEditor.css';
import { escapeHtml } from './textEditorBodyUtils';
import {
  markdownToHtml,
  normalizeCommentBodyArtifacts,
  commentBodyIsEffectivelyEmpty,
  applyRichEditorThemeColorAdaptation,
  getEditorThemeDefaultTextColor,
} from './textEditorBodyUtils';
import { sanitizeHtml } from '../../../utils/sanitizeHtml';
import { runTextEditorExport, runTextEditorPrint } from './textEditorExport';
import {
  TEXT_EDITOR_DEFAULT_PAGE_ORIENTATION,
  TEXT_EDITOR_PAGE_ORIENTATION,
} from './textEditorPageOrientation';
import {
  TEXT_EDITOR_DEFAULT_PAGE_LAYOUT_MODE,
  TEXT_EDITOR_DEFAULT_CUSTOM_PRINT_MARGINS_IN,
  TEXT_EDITOR_DEFAULT_PRINT_MARGIN_PRESET,
  TEXT_EDITOR_DEFAULT_PRINT_PAGE_NUMBERS_ENABLED,
  TEXT_EDITOR_PAGE_LAYOUT_MODE,
  TEXT_EDITOR_PAGE_SHEET_GAP_PX,
  TEXT_EDITOR_PRINT_MARGIN_PRESET,
  countTextEditorDocumentPages,
  getTextEditorPageContentStepPx,
  getTextEditorPagePreviewScale,
  getTextEditorPageSheetOuterHeightPx,
  getTextEditorPageSheetOuterWidthPx,
  shouldIncludePageNumbersInExport,
} from './textEditorPageLayout';
import {
  TEXT_EDITOR_MENU_PORTAL_Z_INDEX,
  TEXT_EDITOR_MOBILE_LAYOUT_MQ,
} from './textEditorMenuPortal';
import {
  formatPrintMarginsSummary,
  getTextEditorPrintMarginStyleProperties,
  normalizeCustomPrintMarginsIn,
  resolveTextEditorPrintMargins,
} from './textEditorPrintLayout';
import TextEditorPrintMarginsModal from './TextEditorPrintMarginsModal';
import {
  getDefaultDocumentPageSettings,
  normalizeDocumentPageSettings,
} from './textEditorDocumentPageSettings';

export {
  markdownToHtml,
  commentBodyLooksLikeStoredHtml,
  normalizeCommentBodyArtifacts,
  repairDiscussionBodyHtml,
  commentBodyToDisplayHtml,
  commentBodyIsEffectivelyEmpty,
} from './textEditorBodyUtils';

function classNames(...parts) {
  return parts.filter(Boolean).join(' ');
}

/** Coalesce ResizeObserver bursts (mobile keyboard / URL bar) to one layout read per frame. */
function scheduleOnNextFrame(frameRef, fn) {
  if (frameRef.current != null) return;
  frameRef.current = requestAnimationFrame(() => {
    frameRef.current = null;
    fn();
  });
}

function scheduleIdle(timerRef, delayMs, fn) {
  if (timerRef.current != null) window.clearTimeout(timerRef.current);
  timerRef.current = window.setTimeout(() => {
    timerRef.current = null;
    fn();
  }, delayMs);
}

function cancelIdle(timerRef) {
  if (timerRef.current == null) return;
  window.clearTimeout(timerRef.current);
  timerRef.current = null;
}

const RICH_INPUT_IDLE_MS = 360;
const PAGE_COUNT_IDLE_MS = 200;

function rowsToPipeMarkdown(rows) {
  if (!rows.length) return '';
  const ncol = Math.max(...rows.map((r) => r.length));
  const pad = (arr) => {
    const a = [...arr];
    while (a.length < ncol) a.push('');
    return a;
  };
  const lines = [];
  lines.push('| ' + pad(rows[0]).join(' | ') + ' |');
  lines.push('| ' + Array.from({ length: ncol }, () => '---').join(' | ') + ' |');
  for (let r = 1; r < rows.length; r++) {
    lines.push('| ' + pad(rows[r]).join(' | ') + ' |');
  }
  return lines.join('\n');
}

function htmlTableToPipeMarkdownFromRegex(tableHtml) {
  const rows = [];
  const trRe = /<tr[^>]*>([\s\S]*?)<\/tr>/gi;
  let trm;
  while ((trm = trRe.exec(tableHtml))) {
    const inner = trm[1];
    const cells = [];
    const cellRe = /<t[dh][^>]*>([\s\S]*?)<\/t[dh]>/gi;
    let cm;
    while ((cm = cellRe.exec(inner))) {
      let t = cm[1].replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
      t = t.replace(/\\/g, '\\\\').replace(/\|/g, '\\|');
      cells.push(t);
    }
    if (cells.length) rows.push(cells);
  }
  return rows.length ? rowsToPipeMarkdown(rows) : '';
}

function htmlTableToPipeMarkdown(tableHtml) {
  if (typeof window !== 'undefined' && typeof DOMParser !== 'undefined') {
    try {
      const doc = new DOMParser().parseFromString(tableHtml, 'text/html');
      const table = doc.querySelector('table');
      if (table) {
        const rows = [];
        table.querySelectorAll('tr').forEach((tr) => {
          const cells = [];
          tr.querySelectorAll('th, td').forEach((cell) => {
            let t = (cell.textContent || '').replace(/\s+/g, ' ').trim();
            t = t.replace(/\\/g, '\\\\').replace(/\|/g, '\\|');
            cells.push(t);
          });
          if (cells.length) rows.push(cells);
        });
        if (rows.length) return rowsToPipeMarkdown(rows);
      }
    } catch {
      /* fall through */
    }
  }
  return htmlTableToPipeMarkdownFromRegex(tableHtml);
}

/**
 * Caret inside a table cell (rich editor). Used for row/column structure actions.
 * @returns {{ table: HTMLTableElement, tr: HTMLTableRowElement, cell: HTMLTableCellElement, cellIndex: number, rowIndex: number } | null}
 */
function getTableCellContextFromSelection(editorRoot) {
  if (!editorRoot) return null;
  const sel = window.getSelection();
  if (!sel?.rangeCount) return null;
  let node = sel.getRangeAt(0).commonAncestorContainer;
  if (node.nodeType === Node.TEXT_NODE) node = node.parentElement;
  if (!node) return null;
  const cell = node.closest?.('td, th');
  if (!cell || !editorRoot.contains(cell)) return null;
  const tr = cell.parentElement;
  const table = cell.closest('table');
  if (!tr || !table || !editorRoot.contains(table)) return null;
  return {
    table,
    tr,
    cell,
    cellIndex: cell.cellIndex,
    rowIndex: tr.rowIndex
  };
}

/**
 * @param {'addRowBelow'|'addColumnRight'|'deleteRow'|'deleteColumn'} action
 * @returns {boolean} whether the DOM was changed
 */
function mutateTableStructure(editorRoot, action) {
  const ctx = getTableCellContextFromSelection(editorRoot);
  if (!ctx) return false;
  const { table, tr, cellIndex } = ctx;

  switch (action) {
    case 'addRowBelow': {
      const refRow = tr;
      const ncol = refRow.cells.length;
      const newRow = table.insertRow(refRow.rowIndex + 1);
      while (newRow.cells.length) newRow.deleteCell(0);
      for (let i = 0; i < ncol; i++) {
        const refCell = refRow.cells[i];
        const tag = refCell?.tagName === 'TH' ? 'th' : 'td';
        const c = document.createElement(tag);
        c.innerHTML = '\u00a0';
        newRow.appendChild(c);
      }
      return true;
    }
    case 'addColumnRight': {
      const insertAfter = cellIndex;
      for (let r = 0; r < table.rows.length; r++) {
        const row = table.rows[r];
        const refCell = row.cells[insertAfter];
        const tag = refCell ? (refCell.tagName === 'TH' ? 'th' : 'td') : row.parentElement?.tagName === 'THEAD' ? 'th' : 'td';
        const c = document.createElement(tag);
        c.innerHTML = '\u00a0';
        if (refCell?.nextSibling) row.insertBefore(c, refCell.nextSibling);
        else if (refCell) row.appendChild(c);
        else row.appendChild(c);
      }
      return true;
    }
    case 'deleteRow': {
      if (table.rows.length <= 1) return false;
      tr.remove();
      return true;
    }
    case 'deleteColumn': {
      if (!table.rows.length) return false;
      if (table.rows[0].cells.length <= 1) return false;
      const col = cellIndex;
      for (let r = 0; r < table.rows.length; r++) {
        const row = table.rows[r];
        if (row.cells[col]) row.deleteCell(col);
      }
      return true;
    }
    default:
      return false;
  }
}

function flattenTableCellsInOrder(table) {
  const out = [];
  for (let r = 0; r < table.rows.length; r++) {
    const row = table.rows[r];
    for (let i = 0; i < row.cells.length; i++) {
      out.push(row.cells[i]);
    }
  }
  return out;
}

/** Above CenterModalWindow (3000) and expanded composer shell so table tools stay visible in modals. */
const TABLE_EDITOR_PORTAL_Z = 1000016;
const FLAG_MENU_PORTAL_Z = 10050;

/** Open flag menu to the right of the trigger; flip below if there is not enough space above. */
function getFlagMenuPortalStyle(triggerRect, menuEl) {
  const GAP = 8;
  const PAD = 8;
  const MIN_WIDTH = 170;
  const menuWidth = menuEl?.offsetWidth || MIN_WIDTH;
  const menuHeight = menuEl?.offsetHeight || 180;

  let left = triggerRect.left;
  let top = Math.max(PAD, triggerRect.top - GAP);
  let transform = 'translate(0, -100%)';

  if (left + menuWidth > window.innerWidth - PAD) {
    left = Math.max(PAD, window.innerWidth - PAD - menuWidth);
  }

  if (top - menuHeight < PAD) {
    top = triggerRect.bottom + GAP;
    transform = 'translate(0, 0)';
  }

  return {
    position: 'fixed',
    top: `${top}px`,
    left: `${left}px`,
    transform,
    zIndex: FLAG_MENU_PORTAL_Z,
    minWidth: `${MIN_WIDTH}px`,
  };
}

/** Place table-properties control above the table (left-aligned); if not enough viewport space above, dock to the left of the table so thead stays visible. */
function getTablePropsFloatingPosition(rect) {
  const GAP = 8;
  const controlSize = 40;
  let top = rect.top - controlSize - GAP;
  let left = rect.left + 4;
  if (top < 4) {
    left = Math.max(4, rect.left - controlSize - GAP);
    top = Math.max(4, rect.top + 4);
  }
  return { left, top };
}

function getTableCellAtCaret(editorRoot) {
  const sel = window.getSelection();
  if (!sel?.rangeCount) return null;
  let node = sel.getRangeAt(0).commonAncestorContainer;
  if (node.nodeType === Node.TEXT_NODE) node = node.parentElement;
  const cell = node?.closest?.('td, th');
  if (!cell || !editorRoot.contains(cell)) return null;
  return cell;
}

function setCaretAtCellStart(cell) {
  const sel = window.getSelection();
  const range = document.createRange();
  range.selectNodeContents(cell);
  range.collapse(true);
  sel.removeAllRanges();
  sel.addRange(range);
}

function placeCaretAfterTable(editorRoot, table) {
  const sel = window.getSelection();
  const range = document.createRange();
  let next = table.nextSibling;
  while (next && next.nodeType === Node.COMMENT_NODE) next = next.nextSibling;

  if (next && editorRoot.contains(next)) {
    if (next.nodeType === Node.ELEMENT_NODE) {
      range.selectNodeContents(next);
      range.collapse(true);
    } else {
      range.setStart(next, 0);
      range.collapse(true);
    }
  } else {
    const p = document.createElement('p');
    p.innerHTML = '<br/>';
    table.parentNode?.insertBefore(p, table.nextSibling);
    range.setStart(p, 0);
    range.collapse(true);
  }
  sel.removeAllRanges();
  sel.addRange(range);
}

function placeCaretBeforeTable(editorRoot, table) {
  const sel = window.getSelection();
  const range = document.createRange();
  let prev = table.previousSibling;
  while (prev && prev.nodeType === Node.COMMENT_NODE) prev = prev.previousSibling;

  if (prev && editorRoot.contains(prev)) {
    if (prev.nodeType === Node.ELEMENT_NODE) {
      range.selectNodeContents(prev);
      range.collapse(false);
    } else {
      const len = (prev.textContent || '').length;
      range.setStart(prev, len);
      range.collapse(true);
    }
  } else {
    const p = document.createElement('p');
    p.innerHTML = '<br/>';
    table.parentNode?.insertBefore(p, table);
    range.setStart(p, 0);
    range.collapse(true);
  }
  sel.removeAllRanges();
  sel.addRange(range);
}

/** Tab = next cell (row-major); Shift+Tab = previous. At edges, caret moves before/after the table. */
function navigateTableTab(editorRoot, backward) {
  const cell = getTableCellAtCaret(editorRoot);
  if (!cell) return false;
  const table = cell.closest('table');
  if (!table || !editorRoot.contains(table)) return false;

  const flat = flattenTableCellsInOrder(table);
  const idx = flat.indexOf(cell);
  if (idx < 0) return false;

  if (!backward) {
    if (idx < flat.length - 1) {
      setCaretAtCellStart(flat[idx + 1]);
      return true;
    }
    placeCaretAfterTable(editorRoot, table);
    return true;
  }

  if (idx > 0) {
    setCaretAtCellStart(flat[idx - 1]);
    return true;
  }
  placeCaretBeforeTable(editorRoot, table);
  return true;
}

function getPreBlockAtCaret(editorRoot, range) {
  let node = range.commonAncestorContainer;
  if (node.nodeType === Node.TEXT_NODE) node = node.parentElement;
  const pre = node?.closest?.('pre');
  if (pre && editorRoot.contains(pre)) return pre;
  return null;
}

function isCollapsedRangeAtBoundaryOfElement(range, containerEl, atStart) {
  if (!range.collapsed) return false;
  const edge = document.createRange();
  edge.selectNodeContents(containerEl);
  edge.collapse(atStart);
  const point = atStart ? Range.START_TO_START : Range.END_TO_END;
  return range.compareBoundaryPoints(point, edge) === 0;
}

function selectionCoversEntirePreContents(range, preEl) {
  const preRange = document.createRange();
  preRange.selectNodeContents(preEl);
  return (
    range.compareBoundaryPoints(Range.START_TO_START, preRange) <= 0 &&
    range.compareBoundaryPoints(Range.END_TO_END, preRange) >= 0
  );
}

/** Backspace at block start or Delete at block end removes the whole `<pre>` (contenteditable quirk). */
function removePreBlockAndPlaceCaret(editorRoot, preEl, placeCaretBefore) {
  const sel = window.getSelection();
  const range = document.createRange();
  const parent = preEl.parentNode;

  if (placeCaretBefore) {
    let prev = preEl.previousSibling;
    while (prev && prev.nodeType === Node.COMMENT_NODE) prev = prev.previousSibling;
    preEl.remove();
    if (prev && editorRoot.contains(prev)) {
      if (prev.nodeType === Node.ELEMENT_NODE) {
        range.selectNodeContents(prev);
        range.collapse(false);
      } else {
        range.setStart(prev, (prev.textContent || '').length);
        range.collapse(true);
      }
    } else if (parent && editorRoot.contains(parent)) {
      const p = document.createElement('p');
      p.innerHTML = '<br/>';
      parent.insertBefore(p, parent.firstChild);
      range.setStart(p, 0);
      range.collapse(true);
    } else {
      editorRoot.innerHTML = '<p><br/></p>';
      range.setStart(editorRoot.firstChild, 0);
      range.collapse(true);
    }
  } else {
    let next = preEl.nextSibling;
    while (next && next.nodeType === Node.COMMENT_NODE) next = next.nextSibling;
    preEl.remove();
    if (next && editorRoot.contains(next)) {
      if (next.nodeType === Node.ELEMENT_NODE) {
        range.selectNodeContents(next);
        range.collapse(true);
      } else {
        range.setStart(next, 0);
        range.collapse(true);
      }
    } else if (parent && editorRoot.contains(parent)) {
      const p = document.createElement('p');
      p.innerHTML = '<br/>';
      parent.appendChild(p);
      range.setStart(p, 0);
      range.collapse(true);
    } else {
      editorRoot.innerHTML = '<p><br/></p>';
      range.setStart(editorRoot.firstChild, 0);
      range.collapse(true);
    }
  }

  sel.removeAllRanges();
  sel.addRange(range);
}

function tryRemoveCodeBlockOnBoundaryKey(editorRoot, event) {
  if (event.key !== 'Backspace' && event.key !== 'Delete') return false;
  if (event.altKey || event.ctrlKey || event.metaKey) return false;

  const sel = window.getSelection();
  if (!sel?.rangeCount) return false;
  const range = sel.getRangeAt(0);
  const pre = getPreBlockAtCaret(editorRoot, range);
  if (!pre) return false;

  if (!range.collapsed) {
    if (!selectionCoversEntirePreContents(range, pre)) return false;
    event.preventDefault();
    removePreBlockAndPlaceCaret(editorRoot, pre, event.key === 'Backspace');
    return true;
  }

  if (event.key === 'Backspace' && isCollapsedRangeAtBoundaryOfElement(range, pre, true)) {
    event.preventDefault();
    removePreBlockAndPlaceCaret(editorRoot, pre, true);
    return true;
  }

  if (event.key === 'Delete' && isCollapsedRangeAtBoundaryOfElement(range, pre, false)) {
    event.preventDefault();
    removePreBlockAndPlaceCaret(editorRoot, pre, false);
    return true;
  }

  return false;
}

/** Root-relative `/uploads/...` vs absolute URLs; safe for HTML `src` after escaping. */
function normalizeAssetUrlForHtmlAttr(u) {
  const s = String(u || '').trim();
  if (!s) return '';
  if (/^data:/i.test(s) || /^blob:/i.test(s)) return s;
  if (/^https?:\/\//i.test(s) || s.startsWith('//')) return s;
  return s.startsWith('/') ? s : `/${s.replace(/^\//, '')}`;
}

function findActiveMentionInPrelude(prelude) {
  let i = prelude.length - 1;
  while (i >= 0) {
    const c = prelude[i];
    if (c === '\n') return null;
    if (c !== '@') {
      i -= 1;
      continue;
    }
    const before = i > 0 ? prelude[i - 1] : '';
    if (before && /[A-Za-z0-9_.]/.test(before)) {
      i -= 1;
      continue;
    }
    const query = prelude.slice(i + 1);
    if (/[\s\n]/.test(query)) return null;
    if (query.length > 200) return null;
    return { atIndex: i, query };
  }
  return null;
}

/** Inserts markdown `[@Firstname](mailto:email)` — notifications use mailto; display shows @Firstname + email line. */
function formatMentionInsert(user) {
  const email = String(user?.email || '').trim();
  const name = String(user?.name || '').trim();
  const first = name.split(/\s+/).filter(Boolean)[0] || email.split('@')[0] || 'user';
  const safeFirst = first.replace(/\]/g, '');
  const safeEmail = email.replace(/\)/g, '');
  if (!safeEmail) return '@';
  return `[@${safeFirst}](mailto:${safeEmail})`;
}

function resolveTextOffsetToPoint(root, offset) {
  let acc = 0;
  const tw = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null);
  let n;
  while ((n = tw.nextNode())) {
    const len = n.textContent.length;
    if (acc + len >= offset) {
      return { node: n, offset: offset - acc };
    }
    acc += len;
  }
  return null;
}

function getPreludeFromRichEditor(root, sel) {
  if (!sel?.rangeCount) return '';
  const r = sel.getRangeAt(0);
  if (!r.collapsed) return '';
  const pre = document.createRange();
  pre.selectNodeContents(root);
  pre.setEnd(r.endContainer, r.endOffset);
  return pre.toString();
}

/** Renders a mention as @display only; email is in title (hover) and mailto (click). */

function htmlBlockquotesToMarkdownLines(html) {
  let s = String(html);
  s = s.replace(
    /<div[^>]*\bsupport-comment-quote\b[^>]*>\s*<span[^>]*\bsupport-comment-quote__glyph\b[^>]*>[\s\S]*?<\/span>\s*<blockquote([^>]*)>([\s\S]*?)<\/blockquote>\s*<\/div>/gi,
    '<blockquote$1>$2</blockquote>'
  );
  return s.replace(/<blockquote([^>]*)>([\s\S]*?)<\/blockquote>/gi, (_m, _attrs, inner) => {
    let t = String(inner || '')
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<\/p>/gi, '\n')
      .replace(/<p[^>]*>/gi, '')
      .replace(/<div[^>]*>/gi, '\n')
      .replace(/<\/div>/gi, '\n');
    t = t.replace(/<[^>]+>/g, '');
    t = t
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&#39;/g, "'")
      .replace(/&quot;/g, '"')
      .trim();
    const lines = t.split(/\r\n|\r|\n/).reduce((acc, line) => {
      const trimmed = line.trim();
      if (trimmed) acc.push(trimmed);
      return acc;
    }, []);
    if (!lines.length) return '\n\n';
    return `\n\n${lines.map((line) => `> ${line}`).join('\n')}\n\n`;
  });
}

function htmlToMarkdown(html = '') {
  let chunk = String(html);
  chunk = chunk.replace(/<table\b[^>]*>[\s\S]*?<\/table>/gi, (full) => {
    const md = htmlTableToPipeMarkdown(full);
    return md ? `\n\n${md}\n\n` : full;
  });

  chunk = htmlBlockquotesToMarkdownLines(chunk);

  const protectedDivWrappers = [];
  chunk = chunk.replace(
    /<div[^>]*\b(?:wfm-text-editor__video-wrap|support-comment-table-wrap|support-comment-quote)\b[^>]*>[\s\S]*?<\/div>/gi,
    (m) => {
      const ph = `\u2060WFMDIV${protectedDivWrappers.length}\u2060`;
      protectedDivWrappers.push(m);
      return ph;
    }
  );

  const lineBreaked = chunk
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n\n')
    .replace(/<\/h[1-6]>/gi, '\n\n')
    .replace(/<\/blockquote>/gi, '\n')
    .replace(/<\/li>/gi, '\n')
    .replace(/<\/(ul|ol)>/gi, '\n')
    /* contenteditable line breaks are often <div> blocks, not <p> */
    .replace(/<div[^>]*>/gi, '\n')
    .replace(/<\/div>/gi, '\n');

  chunk = lineBreaked;
  protectedDivWrappers.forEach((html, i) => {
    const ph = `\u2060WFMDIV${i}\u2060`;
    if (chunk.includes(ph)) chunk = chunk.split(ph).join(html);
  });

  const markdown = chunk
    .replace(/<h1>(.*?)<\/h1>/gi, '# $1')
    .replace(/<h2>(.*?)<\/h2>/gi, '## $1')
    .replace(/<h3>(.*?)<\/h3>/gi, '### $1')
    .replace(/<h4>(.*?)<\/h4>/gi, '#### $1')
    .replace(/<h5>(.*?)<\/h5>/gi, '##### $1')
    .replace(/<h6>(.*?)<\/h6>/gi, '###### $1')
    .replace(/<strong>(.*?)<\/strong>/gi, '**$1**')
    .replace(/<b>(.*?)<\/b>/gi, '**$1**')
    .replace(/<em>(.*?)<\/em>/gi, '*$1*')
    .replace(/<i>(.*?)<\/i>/gi, '*$1*')
    .replace(/<u>(.*?)<\/u>/gi, '__$1__')
    .replace(/<a\s+([^>]+)>([^<]*)<\/a>/gi, (full, attrs, inner) => {
      if (!/wfm-mention--link/.test(attrs)) return full;
      const hm = /href="([^"]+)"/i.exec(attrs);
      if (!hm) return full;
      let em = hm[1].replace(/^mailto:/i, '');
      try {
        em = decodeURIComponent(em);
      } catch {
        /* keep raw */
      }
      const name = String(inner || '')
        .replace(/^@/, '')
        .trim();
      return `[@${name}](mailto:${em})`;
    })
    .replace(/<a[^>]*href="([^"]+)"[^>]*>(.*?)<\/a>/gi, '[$2]($1)')
    .replace(/<code>(.*?)<\/code>/gi, '`$1`')
    .replace(/<pre><code>([\s\S]*?)<\/code><\/pre>/gi, '```\n$1\n```')
    .replace(/<li>(.*?)$/gim, '- $1')
    .replace(/<span class="wfm-mention wfm-mention-display"[^>]*>([^<]*)<\/span>/gi, '$1')
    .replace(/<span class="wfm-mention">([^<]*)<\/span>/gi, '$1')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&#39;/g, "'")
    .replace(/&quot;/g, '"')
    .replace(/\n{3,}/g, '\n\n')
    .trim();

  return markdown;
}

const FLAG_INFO_SPOILER =
  'Turn on when this reveals plot points or surprises. Readers see a heads-up and can skip if they prefer.';
const FLAG_INFO_AGE =
  'Turn on for mature content suitable for adults. Comments may be hidden or limited for under-18 viewers.';
const FLAG_INFO_SENSITIVITY =
  'Turn on when topics may be emotionally intense or difficult so readers know what to expect.';

function ContentFlagInfoTip({ text, label }) {
  return (
    <Tooltip text={text} position="top">
      <span className="wfm-text-editor__flag-row-info" aria-label={`${label}: more information`}>
        <IoMdInformationCircleOutline aria-hidden focusable="false" />
      </span>
    </Tooltip>
  );
}

function ContentFlagMenu({
  isSpoiler,
  isAgeRestricted,
  isSensitivity,
  onSpoilerChange,
  onAgeRestrictedChange,
  onSensitivityChange,
  onOpenChange,
  className
}) {
  const containerRef = useRef(null);
  const menuPortalRef = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState(null);

  const syncMenuPosition = useCallback(() => {
    const trigger = containerRef.current;
    if (!trigger || typeof window === 'undefined') return;
    const rect = trigger.getBoundingClientRect();
    setMenuStyle(getFlagMenuPortalStyle(rect, menuPortalRef.current));
  }, []);

  useEffect(() => {
    onOpenChange?.(menuOpen);
  }, [menuOpen, onOpenChange]);

  useLayoutEffect(() => {
    if (!menuOpen) {
      setMenuStyle(null);
      return undefined;
    }
    syncMenuPosition();
    window.addEventListener('resize', syncMenuPosition);
    window.addEventListener('scroll', syncMenuPosition, true);
    return () => {
      window.removeEventListener('resize', syncMenuPosition);
      window.removeEventListener('scroll', syncMenuPosition, true);
    };
  }, [menuOpen, syncMenuPosition]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDoc = (e) => {
      if (containerRef.current?.contains(e.target)) return;
      if (menuPortalRef.current?.contains(e.target)) return;
      setMenuOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  const active = isSpoiler || isAgeRestricted || isSensitivity;

  const flagMenuEl = menuOpen ? (
    <ul
      ref={menuPortalRef}
      className={classNames(
        'gb__menu',
        'gb__menu--left',
        'wfm-text-editor__flag-grouped-menu',
        'wfm-text-editor__flag-grouped-menu--portaled'
      )}
      style={menuStyle || undefined}
      role="menu"
      onMouseDown={(e) => e.stopPropagation()}
    >
      <li role="none" className="gb__menu-item-wrap">
        <div
          className="gb__menu-item wfm-text-editor__flag-menu-toggle-row"
          role="group"
          aria-label={`Spoiler flag, ${isSpoiler ? 'on' : 'off'}`}
        >
          <ContentFlagInfoTip text={FLAG_INFO_SPOILER} label="Spoiler" />
          <span className="gb__menu-item-icon" aria-hidden>
            <MdVisibilityOff />
          </span>
          <span className="gb__menu-item-text wfm-text-editor__flag-menu-toggle-label">Spoiler</span>
          <ToggleButton
            defaultState={isSpoiler}
            onToggle={onSpoilerChange}
            className="wfm-text-editor__flag-toggle"
          />
        </div>
      </li>
      <li role="none" className="gb__menu-item-wrap">
        <div
          className="gb__menu-item wfm-text-editor__flag-menu-toggle-row"
          role="group"
          aria-label={`18 plus content flag, ${isAgeRestricted ? 'on' : 'off'}`}
        >
          <ContentFlagInfoTip text={FLAG_INFO_AGE} label="18 plus" />
          <span className="gb__menu-item-icon" aria-hidden>
            <MdOutline18UpRating />
          </span>
          <span className="gb__menu-item-text wfm-text-editor__flag-menu-toggle-label">18+</span>
          <ToggleButton
            defaultState={isAgeRestricted}
            onToggle={onAgeRestrictedChange}
            className="wfm-text-editor__flag-toggle"
          />
        </div>
      </li>
      <li role="none" className="gb__menu-item-wrap">
        <div
          className="gb__menu-item wfm-text-editor__flag-menu-toggle-row"
          role="group"
          aria-label={`Sensitivity content flag, ${isSensitivity ? 'on' : 'off'}`}
        >
          <ContentFlagInfoTip text={FLAG_INFO_SENSITIVITY} label="Sensitivity" />
          <span className="gb__menu-item-icon" aria-hidden>
            <MdPsychology />
          </span>
          <span className="gb__menu-item-text wfm-text-editor__flag-menu-toggle-label">Sensitivity</span>
          <ToggleButton
            defaultState={isSensitivity}
            onToggle={onSensitivityChange}
            className="wfm-text-editor__flag-toggle"
          />
        </div>
      </li>
    </ul>
  ) : null;

  return (
    <div
      ref={containerRef}
      className={classNames('gb', 'wfm-text-editor__flag-grouped', active && 'is-active', className)}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <CollapseTextButton
        type="button"
        shape="pill"
        variant="outline"
        size="sm"
        color="gray"
        className="wfm-text-editor__flag-ctb"
        iconStart={<FaFlag aria-hidden />}
        aria-haspopup="menu"
        aria-expanded={menuOpen}
        aria-label="Content flags: spoiler, age restriction, sensitivity"
        onMouseDown={(e) => {
          e.stopPropagation();
          if (e.button !== 0) return;
          if (menuOpen) {
            setMenuOpen(false);
            setMenuStyle(null);
            return;
          }
          const rect = containerRef.current?.getBoundingClientRect();
          if (rect) {
            setMenuStyle(getFlagMenuPortalStyle(rect));
          }
          setMenuOpen(true);
        }}
      >
        Flag
      </CollapseTextButton>
      {typeof document !== 'undefined' && flagMenuEl
        ? createPortal(flagMenuEl, document.body)
        : null}
    </div>
  );
}

/** Free / system fonts available in the rich-text toolbar (Google Fonts are OFL or Apache 2.0). */
const EDITOR_FONT_FAMILIES = [
  { value: '', label: 'Font' },
  { value: 'inherit', label: 'Default' },
  { value: 'Arial, Helvetica, sans-serif', label: 'Arial' },
  { value: 'Georgia, "Times New Roman", serif', label: 'Georgia' },
  { value: '"Times New Roman", Times, serif', label: 'Times New Roman' },
  { value: 'Verdana, Geneva, sans-serif', label: 'Verdana' },
  { value: '"Courier New", Courier, monospace', label: 'Courier New' },
  { value: 'Inter, system-ui, sans-serif', label: 'Inter' },
  { value: 'Roboto, system-ui, sans-serif', label: 'Roboto' },
  { value: '"Open Sans", system-ui, sans-serif', label: 'Open Sans' },
  { value: 'Lato, system-ui, sans-serif', label: 'Lato' },
  { value: 'Merriweather, Georgia, serif', label: 'Merriweather' },
  { value: 'Nunito, system-ui, sans-serif', label: 'Nunito' },
  { value: 'Poppins, system-ui, sans-serif', label: 'Poppins' },
];

const EDITOR_FONT_SIZES = [
  { value: '', label: 'Size' },
  { value: '10px', label: '10' },
  { value: '12px', label: '12' },
  { value: '14px', label: '14' },
  { value: '16px', label: '16' },
  { value: '18px', label: '18' },
  { value: '20px', label: '20' },
  { value: '24px', label: '24' },
  { value: '28px', label: '28' },
  { value: '32px', label: '32' },
];

const EDITOR_FONT_FAMILY_OPTIONS = EDITOR_FONT_FAMILIES.filter((opt) => opt.value !== '');
const EDITOR_FONT_SIZE_OPTIONS = EDITOR_FONT_SIZES.filter((opt) => opt.value !== '');

const EDITOR_FONT_SIZE_MIN_PX = 8;
const EDITOR_FONT_SIZE_MAX_PX = 96;

function normalizeEditorFontSizeValue(raw) {
  const s = String(raw ?? '').trim().toLowerCase();
  if (!s) return '';
  const match = s.match(/^(\d+(?:\.\d+)?)\s*(px|pt)?$/);
  if (!match) return null;
  let num = parseFloat(match[1]);
  if (match[2] === 'pt') {
    num = Math.round(((num * 96) / 72) * 100) / 100;
  }
  if (!Number.isFinite(num)) return null;
  num = Math.min(EDITOR_FONT_SIZE_MAX_PX, Math.max(EDITOR_FONT_SIZE_MIN_PX, num));
  const rounded = Math.round(num * 100) / 100;
  return `${rounded}px`;
}

function formatEditorFontSizeInputValue(cssValue) {
  if (!cssValue) return '';
  const m = String(cssValue).trim().match(/^(\d+(?:\.\d+)?)\s*px$/i);
  return m ? m[1] : '';
}

function editorFontFamilySelectedLabel(value) {
  return EDITOR_FONT_FAMILIES.find((opt) => opt.value === value)?.label ?? 'Font';
}

function readResolvedEditorTheme() {
  return typeof document !== 'undefined' && document.documentElement?.getAttribute('data-theme') === 'dark'
    ? 'dark'
    : 'light';
}

/** A range's ancestor may be the contenteditable root; Element.contains() is false for the root itself. */
function rangeIsWithinEditor(editorEl, range) {
  if (!editorEl || !range) return false;
  try {
    const node = range.commonAncestorContainer;
    if (node === editorEl) return true;
    return editorEl.contains(node);
  } catch {
    return false;
  }
}

function rangeHasSelectedContent(range) {
  if (!range || range.collapsed) return false;
  const text = range.toString();
  if (text && text.replace(/\u200b/g, '').length > 0) return true;
  const fragment = range.cloneContents();
  return Boolean(
    fragment.querySelector?.(
      'img,video,br,table,ul,ol,li,blockquote,pre,figure,svg'
    )
  );
}

/** Remember the last in-editor selection (toolbar controls steal focus and collapse it). */
function captureRichEditorSelection(editorEl, selectionRef, nonEmptySelectionRef) {
  if (!editorEl || !selectionRef) return;
  const selection = window.getSelection();
  if (!selection?.rangeCount) return;
  const range = selection.getRangeAt(0);
  if (!rangeIsWithinEditor(editorEl, range)) return;
  selectionRef.current = range.cloneRange();
  if (nonEmptySelectionRef && rangeHasSelectedContent(range)) {
    nonEmptySelectionRef.current = range.cloneRange();
  }
}

const RICH_TYPOGRAPHY_BLOCK_TAGS = new Set([
  'P',
  'DIV',
  'H1',
  'H2',
  'H3',
  'H4',
  'H5',
  'H6',
  'LI',
  'UL',
  'OL',
  'BLOCKQUOTE',
  'PRE',
  'TABLE',
  'TBODY',
  'THEAD',
  'TR',
  'TD',
  'TH',
  'FIGURE',
]);

function applyStylesToElement(el, styleObj) {
  Object.entries(styleObj).forEach(([key, value]) => {
    if (value != null && value !== '') el.style[key] = value;
  });
}

function createStyledSpan(styleObj) {
  const span = document.createElement('span');
  applyStylesToElement(span, styleObj);
  return span;
}

function richFragmentHasBlockNodes(fragment) {
  return Array.from(fragment.childNodes).some(
    (child) => child.nodeType === Node.ELEMENT_NODE && RICH_TYPOGRAPHY_BLOCK_TAGS.has(child.tagName)
  );
}

/** Apply font/color to a node tree without flattening line breaks or inline markup. */
function applyTypographyPreservingStructure(node, styleObj) {
  if (node.nodeType === Node.TEXT_NODE) {
    if (!node.textContent) return document.createTextNode('');
    const span = createStyledSpan(styleObj);
    span.appendChild(node.cloneNode());
    return span;
  }
  if (node.nodeType !== Node.ELEMENT_NODE) return node.cloneNode(true);

  const tag = node.tagName;
  if (tag === 'BR') return node.cloneNode();

  const el = node.cloneNode(false);

  if (RICH_TYPOGRAPHY_BLOCK_TAGS.has(tag)) {
    Array.from(node.childNodes).forEach((child) => {
      el.appendChild(applyTypographyPreservingStructure(child, styleObj));
    });
    return el;
  }

  applyStylesToElement(el, styleObj);
  Array.from(node.childNodes).forEach((child) => {
    el.appendChild(applyTypographyPreservingStructure(child, styleObj));
  });
  return el;
}

function wrapExtractedContentsPreservingStructure(fragment, styleObj) {
  if (!richFragmentHasBlockNodes(fragment)) {
    const span = createStyledSpan(styleObj);
    span.appendChild(fragment);
    return span;
  }
  const wrapped = document.createDocumentFragment();
  Array.from(fragment.childNodes).forEach((child) => {
    wrapped.appendChild(applyTypographyPreservingStructure(child, styleObj));
  });
  return wrapped;
}

function placeCaretAfterNode(selection, node) {
  const after = document.createRange();
  after.setStartAfter(node);
  after.collapse(true);
  selection.removeAllRanges();
  selection.addRange(after);
}

/** execCommand fontName face (first family only). */
const FONT_FAMILY_EXEC_FACE = {
  Arial: 'Arial',
  Georgia: 'Georgia',
  'Times New Roman': 'Times New Roman',
  Verdana: 'Verdana',
  'Courier New': 'Courier New',
  Inter: 'Inter',
  Roboto: 'Roboto',
  'Open Sans': 'Open Sans',
  Lato: 'Lato',
  Merriweather: 'Merriweather',
  Nunito: 'Nunito',
  Poppins: 'Poppins',
};

function fontFamilyToExecFace(fontFamily) {
  const first = String(fontFamily || '')
    .split(',')[0]
    .replace(/['"]/g, '')
    .trim();
  return FONT_FAMILY_EXEC_FACE[first] || first;
}

function applyFontFamilyExecCommand(editorEl, workingRange, fontFamily, selectionRef) {
  const selection = window.getSelection();
  if (!selection) return false;
  selection.removeAllRanges();
  selection.addRange(workingRange);
  document.execCommand('styleWithCSS', false, true);
  const ok = document.execCommand('fontName', false, fontFamilyToExecFace(fontFamily));
  if (ok && fontFamily) {
    const root = editorEl;
    const face = fontFamilyToExecFace(fontFamily);
    root.querySelectorAll('font[face]').forEach((node) => {
      if (String(node.getAttribute('face') || '').toLowerCase() === face.toLowerCase()) {
        node.style.fontFamily = fontFamily;
      }
    });
    root.querySelectorAll('span[style*="font-family"]').forEach((node) => {
      if (!node.style.fontFamily || node.style.fontFamily === face) {
        node.style.fontFamily = fontFamily;
      }
    });
  }
  captureRichEditorSelection(editorEl, selectionRef);
  return ok;
}

function plainTextLenFromHtml(html) {
  return String(html || '')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;|&#160;|\u00a0/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim().length;
}

function shouldHydrateRichDomFromState(root, liveHtml, normalizedContent) {
  if (liveHtml === normalizedContent) return false;
  const active = document.activeElement;
  if (active === root || root.contains(active)) return false;
  if (active?.closest?.('.wfm-text-editor')) return false;
  if (liveHtml.trim() && normalizedContent.trim()) {
    if (commentBodyIsEffectivelyEmpty(liveHtml) && !commentBodyIsEffectivelyEmpty(normalizedContent)) {
      return true;
    }
    return false;
  }
  /* Do not overwrite DOM when the editor has newer typing than React state (Save / blur race). */
  if (plainTextLenFromHtml(liveHtml) > plainTextLenFromHtml(normalizedContent)) return false;
  return true;
}

/**
 * Apply typography using a Range directly (does not depend on window.getSelection after toolbar blur).
 * Preserves paragraphs, line breaks, lists, and nested inline formatting.
 */
function applyRichTypographyToRange(editorEl, styleObj, range, selectionRef) {
  if (!editorEl || !styleObj || !range) return false;

  const hasStyle = Object.values(styleObj).some((v) => v != null && v !== '');
  if (!hasStyle) return false;

  let workingRange;
  try {
    workingRange = range.cloneRange();
    if (!rangeIsWithinEditor(editorEl, workingRange)) return false;
  } catch {
    return false;
  }

  editorEl.focus({ preventScroll: true });

  const selection = window.getSelection();
  if (selection) {
    selection.removeAllRanges();
    selection.addRange(workingRange);
  }

  if (workingRange.collapsed) {
    if (styleObj.color && selection) {
      selection.removeAllRanges();
      selection.addRange(workingRange);
      document.execCommand('styleWithCSS', false, true);
      document.execCommand('foreColor', false, styleObj.color);
      captureRichEditorSelection(editorEl, selectionRef);
      return true;
    }
    const span = createStyledSpan(styleObj);
    span.appendChild(document.createTextNode('\u200b'));
    workingRange.insertNode(span);
    if (selection) {
      const cursor = document.createRange();
      cursor.setStart(span.firstChild, 1);
      cursor.collapse(true);
      selection.removeAllRanges();
      selection.addRange(cursor);
    }
    captureRichEditorSelection(editorEl, selectionRef);
    return true;
  }

  if (styleObj.fontFamily && applyFontFamilyExecCommand(editorEl, workingRange, styleObj.fontFamily, selectionRef)) {
    return true;
  }

  /* Never use insertHTML span-wrap — it merges lines and drops spaces (invalid span around blocks). */
  const fragment = workingRange.extractContents();
  if (!fragment.childNodes.length && !fragment.textContent) return false;

  const insertedNode = wrapExtractedContentsPreservingStructure(fragment, styleObj);
  let caretTarget = insertedNode;
  if (insertedNode.nodeType === Node.DOCUMENT_FRAGMENT_NODE) {
    caretTarget = insertedNode.lastChild;
  }
  workingRange.insertNode(insertedNode);

  if (selection && caretTarget?.parentNode) {
    placeCaretAfterNode(selection, caretTarget);
  }

  captureRichEditorSelection(editorEl, selectionRef);
  return true;
}

const TOOLBAR_ITEMS = [
  { key: 'bold', label: 'Bold', icon: FaBold, command: 'bold' },
  { key: 'italic', label: 'Italic', icon: FaItalic, command: 'italic' },
  { key: 'underline', label: 'Underline', icon: FaUnderline, command: 'underline' },
  { key: 'subscript', label: 'X1', icon: MdSubscript, command: 'subscript' },
  { key: 'superscript', label: 'X2', icon: MdSuperscript, command: 'superscript' },
  { key: 'increase-font', label: 'A+', icon: MdTextIncrease, command: 'increaseFontSize' },
  { key: 'decrease-font', label: 'A-', icon: MdTextDecrease, command: 'decreaseFontSize' },
  { key: 'heading', label: 'Heading', icon: FaHeading, command: 'heading' },
  { key: 'link', label: 'Link', icon: FaLink, command: 'createLink' },
  { key: 'image', label: 'Image', icon: FaImage, command: 'insertImage' },
  { key: 'video', label: 'Video', icon: FaVideo, command: 'insertVideo' },
  { key: 'numbered', label: 'Numbered List', icon: FaListOl, command: 'insertOrderedList' },
  { key: 'ordered', label: 'Ordered List', icon: FaListUl, command: 'insertUnorderedList' },
  { key: 'quote', label: 'Quote', icon: FaQuoteRight, command: 'formatBlock', value: 'blockquote' },
  { key: 'code', label: 'Code Snippet', icon: FaCode, command: 'insertCode' },
  { key: 'table', label: 'Table', icon: FaTable, command: 'insertTable' }
];

/**
 * Reusable rich + markdown editor.
 *
 * @param {object} props
 * @param {string} [props.initialHtml]
 * @param {string} [props.initialMarkdown]
 * @param {string} [props.className]
 * @param {(payload: {
 *   html: string,
 *   markdown: string,
 *   mode: 'rich'|'markdown',
 *   theme: 'light'|'dark',
 *   spoiler: boolean,
 *   ageRestricted: boolean,
 *   sensitivity: boolean,
 *   commentsDisabled: boolean
 * }) => void} [props.onChange]
 * @param {(q: string, signal?: AbortSignal) => Promise<Array<{ userId?: string; name: string; email: string }>>} [props.mentionUserSearch]
 * @param {boolean} [props.showContentFlags=true] — Spoiler / 18+ toolbar + notices (disable for threaded comments).
 * @param {boolean} [props.initialSpoiler=false]
 * @param {boolean} [props.initialAgeRestricted=false]
 * @param {boolean} [props.initialSensitivity=false]
 * @param {(file: File) => Promise<string>} [props.richMediaUpload] — Upload image/video files and return a URL for `src` (avoids huge base64 in JSON comment bodies).
 * @param {string} [props.primaryActionText]
 * @param {() => void} [props.onPrimaryAction]
 * @param {boolean} [props.primaryActionDisabled]
 * @param {boolean} [props.primaryActionsOnRight=false] — flags/toolbar left; primary + secondary actions right (e.g. comment composers).
 * @param {string} [props.primaryActionClassName] — extra class on the primary action button.
 * @param {string} [props.secondaryActionText]
 * @param {() => void} [props.onSecondaryAction]
 * @param {boolean} [props.secondaryActionDisabled]
 * @param {{
 *   toolbar?: boolean,
 *   entireToolbar?: boolean,
 *   flag?: boolean,
 *   cancel?: boolean,
 *   comment?: boolean,
 *   primaryAction?: boolean,
 *   otherButtons?: boolean,
 *   secondaryAction?: boolean,
 * }} [props.visible] — Toggle controls with true/false. Default: all visible.
 * @param {string} [props.richPlaceholder='Write your comment...']
 * @param {string} [props.markdownPlaceholder='Write in markdown...']
 * @param {'rich'|'markdown'} [props.defaultMode='rich'] — Initial editor mode.
 * @param {boolean} [props.documentExport=false] — Show HTML / Word / PDF export and portrait / landscape page layout in rich mode.
 * @param {string} [props.exportBaseFileName='document'] — Base download name (extension added per format).
 * @remarks On viewports ≤767px, an expand control fills the screen with the editor; Escape collapses (mentions dismiss first).
 *   For @mention on the same viewports, a CenterModal with a search field and result list (same API as desktop) — OK inserts the pick.
 */

/** Sticky page chrome (support header, posts shell, etc.) — expanded editor sits below this, not under it. */
const EXPANDED_HEADING_SELECTORS = [
  '.products-sticky-header',
  '.support-header-bar',
  '.support-open-disc-shell-mobile-chrome',
  '.support-open-disc-shell-header',
  '.layout-header',
  '.header-container',
];

function measureProductsHeaderInset() {
  if (typeof document === 'undefined') return 0;
  const header = document.querySelector('.products-sticky-header');
  if (!(header instanceof HTMLElement)) return 0;
  const rect = header.getBoundingClientRect();
  if (rect.height < 8) return 0;
  return Math.max(0, Math.ceil(rect.bottom));
}

function measureExpandedTopInset(hostEl, options = {}) {
  if (typeof document === 'undefined') return 0;

  const productsInset = measureProductsHeaderInset();
  if (options.autoMobileExpandOnFocus) {
    return productsInset;
  }

  /* Create post is already a full-screen sheet on mobile — page chrome inset leaves a gray band. */
  if (options.fromCreatePostModal || hostEl?.closest?.('.support-open-disc-create-post-modal')) {
    return 0;
  }

  const editModal = hostEl?.closest?.('.support-open-disc-edit-post-modal');
  if (editModal instanceof HTMLElement) {
    let inset = 0;
    editModal.querySelectorAll('.modal-header, .support-open-disc-edit-post-modal-head').forEach((el) => {
      if (!(el instanceof HTMLElement)) return;
      const rect = el.getBoundingClientRect();
      if (rect.height < 4) return;
      inset = Math.max(inset, Math.ceil(rect.bottom));
    });
    return inset;
  }

  let inset = 0;
  EXPANDED_HEADING_SELECTORS.forEach((selector) => {
    document.querySelectorAll(selector).forEach((el) => {
      if (!(el instanceof HTMLElement)) return;
      const rect = el.getBoundingClientRect();
      if (rect.height < 8 || rect.width < 8) return;
      if (rect.top > 56) return;
      inset = Math.max(inset, Math.ceil(rect.bottom));
    });
  });
  return inset;
}

export default function TextEditor({
  initialHtml = '',
  initialMarkdown = '',
  className,
  onChange,
  mentionUserSearch,
  showContentFlags = true,
  initialSpoiler = false,
  initialAgeRestricted = false,
  initialSensitivity = false,
  richMediaUpload,
  primaryActionText = '',
  onPrimaryAction,
  primaryActionDisabled = false,
  primaryActionsOnRight = false,
  primaryActionClassName = '',
  secondaryActionText = '',
  onSecondaryAction,
  secondaryActionDisabled = false,
  visible = {},
  richPlaceholder = 'Write your comment...',
  markdownPlaceholder = 'Write in markdown...',
  defaultMode = 'rich',
  defaultToolbarVisible = false,
  /** Collapse to a single line until focus; then reveal toolbar with a smooth expand (post comments). */
  composeExpandOnFocus = false,
  /** On narrow viewports, open the full-screen editor shell when the field is focused (keeps composer above the mobile keyboard). */
  autoMobileExpandOnFocus = false,
  /** Focus the typing surface after mount (e.g. create-post modal). */
  autoFocus = false,
  /** When true, Ctrl/Cmd+Enter triggers onPrimaryAction (plain Enter inserts a newline). */
  submitOnModifierEnter = false,
  richHtmlSnapshotRef,
  /** When true, hide rich/markdown switching (e.g. file type–locked document editors). */
  lockEditorMode = false,
  documentExport = false,
  exportBaseFileName = 'document',
  initialDocumentPageSettings = null,
  /** Document file menu (rich + documentExport). Save disabled when logged in and autosave is on. */
  fileMenuUserLoggedIn = false,
  fileMenuAutosaveEnabled = false,
  onFileMenuSave,
  onFileMenuSaveAsStorageBox,
  onFileMenuSaveAsLocal,
  onFileMenuNew,
  onFileMenuExit,
  onFileMenuDocumentLoaded,
}) {
  const editorRef = useRef(null);
  const editorShellRef = useRef(null);
  const richComposerRef = useRef(null);
  const composeMoreMenuOpenRef = useRef(false);
  const composeFlagMenuOpenRef = useRef(false);
  const skipComposeCollapseRef = useRef(false);
  const savedRichSelectionRef = useRef(null);
  const lastNonEmptyRichSelectionRef = useRef(null);
  const pendingTypographyRangeRef = useRef(null);
  const richToolbarOverlayRef = useRef(null);
  const toolbarScrollRef = useRef(null);
  const surfaceWrapRef = useRef(null);
  const modalFileInputRef = useRef(null);
  const markdownTextareaRef = useRef(null);
  const mentionPopoverRef = useRef(null);
  const mentionAbortRef = useRef(null);
  const mentionTimerRef = useRef(null);
  const mentionRef = useRef(null);
  /** Latest flush of DOM → state; used before leaving expanded (portal) so content survives remount. */
  const flushDomToStateRef = useRef(() => {});
  const [mode, setMode] = useState(() => (defaultMode === 'markdown' ? 'markdown' : 'rich'));
  const [isSpoiler, setIsSpoiler] = useState(() => Boolean(initialSpoiler));
  const [isAgeRestricted, setIsAgeRestricted] = useState(() => Boolean(initialAgeRestricted));
  const [isSensitivity, setIsSensitivity] = useState(() => Boolean(initialSensitivity));
  const initialRichHtml = initialHtml || markdownToHtml(initialMarkdown);
  const [htmlContent, setHtmlContent] = useState(initialRichHtml);
  const [markdownContent, setMarkdownContent] = useState(initialMarkdown || htmlToMarkdown(initialHtml));
  const [isRichContentEmpty, setIsRichContentEmpty] = useState(() =>
    commentBodyIsEffectivelyEmpty(initialRichHtml)
  );
  const initialPageSettings = useMemo(
    () =>
      documentExport
        ? normalizeDocumentPageSettings(initialDocumentPageSettings || getDefaultDocumentPageSettings())
        : getDefaultDocumentPageSettings(),
    [documentExport, initialDocumentPageSettings]
  );

  const [pageOrientation, setPageOrientation] = useState(
    () => initialPageSettings.pageOrientation
  );
  const [pageLayoutMode, setPageLayoutMode] = useState(() => initialPageSettings.pageLayoutMode);
  const [printMarginPreset, setPrintMarginPreset] = useState(
    () => initialPageSettings.printMarginPreset
  );
  const [customPrintMarginsIn, setCustomPrintMarginsIn] = useState(
    () => ({ ...initialPageSettings.customPrintMarginsIn })
  );
  const [printPageNumbersEnabled, setPrintPageNumbersEnabled] = useState(
    () => initialPageSettings.printPageNumbersEnabled
  );
  const [isPrintMarginsModalOpen, setIsPrintMarginsModalOpen] = useState(false);
  const pageSettingsChangeSkipRef = useRef(
    documentExport && initialDocumentPageSettings == null
  );
  const [documentPageCount, setDocumentPageCount] = useState(1);
  const [pagePreviewScale, setPagePreviewScale] = useState(1);
  const pagePreviewScaleFrameRef = useRef(null);
  const documentPageCountIdleRef = useRef(null);
  const richInputIdleRef = useRef(null);
  const htmlContentRef = useRef(initialRichHtml);
  htmlContentRef.current = htmlContent;
  const [modalState, setModalState] = useState({
    isOpen: false,
    type: null,
    title: '',
    fields: []
  });
  const [modalValues, setModalValues] = useState({});
  const [modalUploadFile, setModalUploadFile] = useState(null);
  const [modalUploadError, setModalUploadError] = useState('');
  const [modalMediaBusy, setModalMediaBusy] = useState(false);
  const [isModalDragOver, setIsModalDragOver] = useState(false);
  const [isMarkdownHelpOpen, setIsMarkdownHelpOpen] = useState(false);
  const [isMobileExpanded, setIsMobileExpanded] = useState(false);
  const composeStartsExpanded =
    !composeExpandOnFocus || !commentBodyIsEffectivelyEmpty(initialRichHtml);
  const [isToolbarVisible, setIsToolbarVisible] = useState(
    composeExpandOnFocus ? false : defaultToolbarVisible
  );
  const [isComposeExpanded, setIsComposeExpanded] = useState(composeStartsExpanded);
  const [toolbarFontFamily, setToolbarFontFamily] = useState('');
  const [toolbarFontSize, setToolbarFontSize] = useState('');
  const [toolbarFontSizeDraft, setToolbarFontSizeDraft] = useState('');
  const [toolbarTextColor, setToolbarTextColor] = useState(() =>
    getEditorThemeDefaultTextColor(readResolvedEditorTheme())
  );
  const [editorTheme, setEditorTheme] = useState(() => readResolvedEditorTheme());
  const [canScrollToolbarLeft, setCanScrollToolbarLeft] = useState(false);
  const [canScrollToolbarRight, setCanScrollToolbarRight] = useState(false);
  /** When expanded, sync box to window.visualViewport so keyboard doesn’t clip the toolbar (100dvh alone is wrong). */
  const [expandedVvBox, setExpandedVvBox] = useState(null);
  /** Bottom edge of sticky page heading (px) — expanded shell starts here with reduced height. */
  const [expandedTopInset, setExpandedTopInset] = useState(0);
  const expandedFromCreatePostRef = useRef(false);
  const [expandedFromCreatePost, setExpandedFromCreatePost] = useState(false);

  const [mention, setMention] = useState(null);
  const [mentionRows, setMentionRows] = useState([]);
  const [mentionLoading, setMentionLoading] = useState(false);
  const [mentionHi, setMentionHi] = useState(0);
  /** Viewport ≤767px: @mentions use a CenterModal + search field + list instead of a floating list. */
  const [isMentionMobileViewport, setIsMentionMobileViewport] = useState(false);
  const [isAutoExpandViewport, setIsAutoExpandViewport] = useState(
    () =>
      typeof window !== 'undefined' && window.matchMedia(TEXT_EDITOR_MOBILE_LAYOUT_MQ).matches
  );
  const [selectedMentionEmail, setSelectedMentionEmail] = useState(null);
  const [mobileMentionQuery, setMobileMentionQuery] = useState('');
  const [mobileMentionList, setMobileMentionList] = useState([]);
  const [mobileMentionListLoading, setMobileMentionListLoading] = useState(false);
  const mobileMentionSearchTimerRef = useRef(null);
  const mobileMentionSearchAbortRef = useRef(null);
  const mobileMentionSearchRef = useRef(null);

  mentionRef.current = mention;

  const controlsVisibility = useMemo(() => {
    const toolbarVisible = visible?.toolbar ?? visible?.entireToolbar ?? true;
    return {
      toolbar: toolbarVisible,
      flag: visible?.flag ?? true,
      cancel: visible?.cancel ?? true,
      comment: visible?.comment ?? visible?.primaryAction ?? true,
      otherButtons: visible?.otherButtons ?? visible?.secondaryAction ?? true,
    };
  }, [visible]);
  const toolbarMounted = controlsVisibility.toolbar ? (composeExpandOnFocus ? true : isToolbarVisible) : false;
  const toolbarRevealed = controlsVisibility.toolbar ? isToolbarVisible : false;

  const mobileMentionSearchId = `wfm-mention-mobile-search-${useId().replace(/:/g, '')}`;
  const isMentionMobileModalOpen = Boolean(mention && mentionUserSearch && isMentionMobileViewport);

  /** Rich mode: table selected for drag-resize UI (handle is portaled to body). */
  const activeTableResizeRef = useRef(null);
  const [tableResizeUi, setTableResizeUi] = useState(null);

  useEffect(() => {
    const syncTheme = () => {
      const nextTheme = readResolvedEditorTheme();
      setEditorTheme(nextTheme);
      setToolbarTextColor(getEditorThemeDefaultTextColor(nextTheme));
    };
    syncTheme();
    const observer = new MutationObserver(syncTheme);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    return () => observer.disconnect();
  }, []);

  const syncEditorThemeColors = useCallback(() => {
    if (mode !== 'rich' || !editorRef.current) return;
    applyRichEditorThemeColorAdaptation(editorRef.current, editorTheme);
  }, [mode, editorTheme]);

  useEffect(() => {
    setToolbarFontSizeDraft(formatEditorFontSizeInputValue(toolbarFontSize));
  }, [toolbarFontSize]);

  const resolvedPrintPageNumbers = useMemo(
    () => shouldIncludePageNumbersInExport(pageLayoutMode, printPageNumbersEnabled),
    [pageLayoutMode, printPageNumbersEnabled]
  );

  const resolvedPrintMargins = useMemo(
    () =>
      resolveTextEditorPrintMargins({
        preset: printMarginPreset,
        customMarginsIn: customPrintMarginsIn,
        pageNumbers: resolvedPrintPageNumbers,
      }),
    [printMarginPreset, customPrintMarginsIn, resolvedPrintPageNumbers]
  );

  useLayoutEffect(() => {
    if (
      !documentExport ||
      mode !== 'rich' ||
      pageLayoutMode !== TEXT_EDITOR_PAGE_LAYOUT_MODE.PAGINATED
    ) {
      setDocumentPageCount(1);
      return undefined;
    }
    const root = editorRef.current;
    if (!root) return undefined;
    const updatePageCount = () => {
      scheduleIdle(documentPageCountIdleRef, PAGE_COUNT_IDLE_MS, () => {
        const rootEl = editorRef.current;
        if (!rootEl) return;
        const forcedMin = Number.parseFloat(rootEl.style.minHeight) || 0;
        const measured = rootEl.scrollHeight;
        const step = getTextEditorPageContentStepPx(
          pageOrientation,
          resolvedPrintMargins,
          resolvedPrintPageNumbers
        );
        /* Forced A4 min-height is not content — using it as scrollHeight adds pages forever. */
        const heightForCount =
          forcedMin > 0 && measured <= forcedMin + 2 ? step : measured;
        const next = Math.min(
          80,
          countTextEditorDocumentPages(
            heightForCount,
            pageOrientation,
            resolvedPrintMargins,
            resolvedPrintPageNumbers
          )
        );
        setDocumentPageCount((prev) => (prev === next ? prev : next));
      });
    };
    updatePageCount();
    const observer = new ResizeObserver(updatePageCount);
    observer.observe(root);
    return () => {
      observer.disconnect();
      cancelIdle(documentPageCountIdleRef);
    };
  }, [documentExport, mode, pageLayoutMode, pageOrientation, resolvedPrintMargins, resolvedPrintPageNumbers]);

  /* Re-sync rich DOM when htmlContent/mode change, and when expand/collapse remounts the surface
     (portal toggle) even if htmlContent is unchanged — otherwise the field briefly shows empty. */
  const syncRichPlaceholderVisibility = useCallback((htmlOverride) => {
    const html =
      htmlOverride !== undefined
        ? htmlOverride
        : normalizeCommentBodyArtifacts(editorRef.current?.innerHTML || '');
    setIsRichContentEmpty(commentBodyIsEffectivelyEmpty(html));
  }, []);

  const updateToolbarScrollButtons = useCallback(() => {
    const scroller = toolbarScrollRef.current;
    if (!scroller) {
      setCanScrollToolbarLeft(false);
      setCanScrollToolbarRight(false);
      return;
    }
    const maxScrollLeft = Math.max(0, scroller.scrollWidth - scroller.clientWidth);
    const left = scroller.scrollLeft > 2;
    const right = maxScrollLeft - scroller.scrollLeft > 2;
    setCanScrollToolbarLeft(left);
    setCanScrollToolbarRight(right);
  }, []);

  const shiftToolbar = useCallback(
    (direction) => {
      const scroller = toolbarScrollRef.current;
      if (!scroller) return;
      const delta = Math.max(120, Math.floor(scroller.clientWidth * 0.55));
      scroller.scrollBy({
        left: direction === 'left' ? -delta : delta,
        behavior: 'smooth'
      });
      requestAnimationFrame(updateToolbarScrollButtons);
    },
    [updateToolbarScrollButtons]
  );

  useLayoutEffect(() => {
    if (!editorRef.current || mode !== 'rich') return;
    const root = editorRef.current;
    /* Live typing owns the DOM — do not re-normalize / re-theme on every state echo. */
    if (root === document.activeElement || root.contains(document.activeElement)) {
      return;
    }
    /* Font/size/color controls take focus; do not overwrite live DOM with stale htmlContent. */
    if (document.activeElement?.closest?.('.wfm-text-editor__typography')) {
      syncRichPlaceholderVisibility(normalizeCommentBodyArtifacts(root.innerHTML || ''));
      syncEditorThemeColors();
      return;
    }
    const normalizedContent = normalizeCommentBodyArtifacts(htmlContent);
    const liveHtml = normalizeCommentBodyArtifacts(root.innerHTML || '');
    if (shouldHydrateRichDomFromState(root, liveHtml, normalizedContent)) {
      root.innerHTML = normalizedContent;
    }
    syncRichPlaceholderVisibility(liveHtml.trim() ? liveHtml : htmlContent);
    syncEditorThemeColors();
  }, [mode, htmlContent, isMobileExpanded, syncRichPlaceholderVisibility, syncEditorThemeColors]);

  useLayoutEffect(() => {
    syncEditorThemeColors();
  }, [editorTheme, syncEditorThemeColors]);

  useEffect(() => {
    if (mode !== 'rich') return undefined;
    const onSelectionChange = () => {
      const root = editorRef.current;
      if (!root) return;
      const active = document.activeElement;
      if (active?.closest?.('.wfm-text-editor__typography')) return;
      const selection = window.getSelection();
      if (!selection?.rangeCount) return;
      const range = selection.getRangeAt(0);
      if (!rangeIsWithinEditor(root, range)) return;
      captureRichEditorSelection(root, savedRichSelectionRef, lastNonEmptyRichSelectionRef);
    };
    document.addEventListener('selectionchange', onSelectionChange);
    return () => document.removeEventListener('selectionchange', onSelectionChange);
  }, [mode, isMobileExpanded]);

  useEffect(() => {
    if (!toolbarMounted || mode !== 'rich') {
      setCanScrollToolbarLeft(false);
      setCanScrollToolbarRight(false);
      return undefined;
    }
    const scroller = toolbarScrollRef.current;
    if (!scroller) return undefined;

    const onScroll = () => updateToolbarScrollButtons();
    scroller.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });

    let resizeObserver;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(onScroll);
      resizeObserver.observe(scroller);
    }

    const raf = requestAnimationFrame(updateToolbarScrollButtons);

    return () => {
      cancelAnimationFrame(raf);
      scroller.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      resizeObserver?.disconnect();
    };
  }, [toolbarMounted, mode, updateToolbarScrollButtons]);

  const updateOutput = useCallback(
    (nextHtml, nextMarkdown, nextMode = mode) => {
      if (!onChange) return;
      const spoiler = showContentFlags ? isSpoiler : false;
      const ageRestricted = showContentFlags ? isAgeRestricted : false;
      const sensitivity = showContentFlags ? isSensitivity : false;
      const htmlOut = normalizeCommentBodyArtifacts(nextHtml);
      const mdOut = normalizeCommentBodyArtifacts(nextMarkdown);
      onChange({
        html: htmlOut,
        markdown: mdOut,
        mode: nextMode,
        theme: editorTheme,
        spoiler,
        ageRestricted,
        sensitivity,
        commentsDisabled: ageRestricted,
        ...(documentExport
          ? {
              documentPageSettings: {
                pageOrientation,
                pageLayoutMode,
                printMarginPreset,
                customPrintMarginsIn: { ...customPrintMarginsIn },
                printPageNumbersEnabled,
              },
            }
          : {}),
      });
    },
    [
      onChange,
      showContentFlags,
      isSpoiler,
      isAgeRestricted,
      isSensitivity,
      mode,
      editorTheme,
      documentExport,
      pageOrientation,
      pageLayoutMode,
      printMarginPreset,
      customPrintMarginsIn,
      printPageNumbersEnabled,
    ]
  );

  useEffect(() => {
    if (!documentExport || !onChange) return;
    if (pageSettingsChangeSkipRef.current) {
      pageSettingsChangeSkipRef.current = false;
      return;
    }
    updateOutput(htmlContent, markdownContent);
  }, [
    documentExport,
    onChange,
    pageOrientation,
    pageLayoutMode,
    printMarginPreset,
    customPrintMarginsIn,
    printPageNumbersEnabled,
    updateOutput,
  ]);

  const updateTableResizeUi = useCallback(() => {
    const table = activeTableResizeRef.current;
    const root = editorRef.current;
    if (!table || !root?.contains(table)) {
      activeTableResizeRef.current = null;
      setTableResizeUi(null);
      return;
    }
    const r = table.getBoundingClientRect();
    setTableResizeUi({ left: r.left, top: r.top, width: r.width, height: r.height });
  }, []);

  const getTableFromEventTarget = useCallback((target) => {
    const root = editorRef.current;
    if (!root || !target?.closest) return null;
    const t = target.closest('table');
    if (t && root.contains(t)) return t;
    return null;
  }, []);

  const handleEditorSurfacePointerDownCapture = useCallback(
    (e) => {
      if (mode !== 'rich') return;
      const tbl = getTableFromEventTarget(e.target);
      if (tbl) {
        activeTableResizeRef.current = tbl;
        requestAnimationFrame(() => updateTableResizeUi());
        return;
      }
      if (editorRef.current?.contains(e.target)) {
        activeTableResizeRef.current = null;
        setTableResizeUi(null);
      }
    },
    [mode, getTableFromEventTarget, updateTableResizeUi]
  );

  useEffect(() => {
    if (!autoMobileExpandOnFocus) return undefined;
    const mq = window.matchMedia(TEXT_EDITOR_MOBILE_LAYOUT_MQ);
    const sync = () => setIsAutoExpandViewport(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, [autoMobileExpandOnFocus]);

  const captureExpandedHostContext = useCallback(() => {
    const fromCreatePost = Boolean(editorShellRef.current?.closest?.('.support-open-disc-create-post-modal'));
    expandedFromCreatePostRef.current = fromCreatePost;
    setExpandedFromCreatePost(fromCreatePost);
  }, []);

  const measureInset = useCallback(
    () =>
      measureExpandedTopInset(editorShellRef.current, {
        autoMobileExpandOnFocus,
        fromCreatePostModal: expandedFromCreatePostRef.current,
      }),
    [autoMobileExpandOnFocus]
  );

  useLayoutEffect(() => {
    const selectors = [
      '.products-sticky-header',
      '.support-header-bar',
      '.support-open-disc-shell-mobile-chrome',
      '.layout-header',
      '.header-container',
    ];
    const sync = () => {
      let bottom = 0;
      selectors.forEach((selector) => {
        document.querySelectorAll(selector).forEach((el) => {
          if (!(el instanceof HTMLElement)) return;
          const rect = el.getBoundingClientRect();
          if (rect.height < 8 || rect.top > 80) return;
          bottom = Math.max(bottom, Math.ceil(rect.bottom));
        });
      });
      document.documentElement.style.setProperty('--text-editor-app-header-height', `${bottom}px`);
      if (bottom > 0) {
        document.documentElement.style.setProperty('--products-sticky-header-height', `${bottom}px`);
      }
    };
    sync();
    window.addEventListener('resize', sync, { passive: true });
    window.visualViewport?.addEventListener('resize', sync);
    return () => {
      window.removeEventListener('resize', sync);
      window.visualViewport?.removeEventListener('resize', sync);
    };
  }, [documentExport, isMobileExpanded]);

  const focusComposerSurface = useCallback(() => {
    if (mode === 'markdown') {
      const textarea = markdownTextareaRef.current;
      if (!textarea) return false;
      textarea.focus({ preventScroll: true });
      const len = textarea.value.length;
      try {
        textarea.setSelectionRange(len, len);
      } catch {
        /* ignore */
      }
      return true;
    }
    const richSurface = editorRef.current;
    if (!richSurface) return false;
    richSurface.focus({ preventScroll: true });
    const sel = window.getSelection();
    if (sel) {
      const range = document.createRange();
      range.selectNodeContents(richSurface);
      range.collapse(false);
      sel.removeAllRanges();
      sel.addRange(range);
    }
    return true;
  }, [mode]);

  useEffect(() => {
    if (!autoFocus) return undefined;
    let cancelled = false;
    let attempts = 0;
    const tryFocus = () => {
      if (cancelled) return;
      if (focusComposerSurface()) return;
      if (attempts >= 30) return;
      attempts += 1;
      requestAnimationFrame(tryFocus);
    };
    const timerId = window.setTimeout(() => {
      requestAnimationFrame(tryFocus);
    }, 0);
    return () => {
      cancelled = true;
      window.clearTimeout(timerId);
    };
  }, [autoFocus, focusComposerSurface]);

  const triggerAutoMobileExpand = useCallback(() => {
    if (!autoMobileExpandOnFocus || !isAutoExpandViewport || isMobileExpanded) return;
    captureExpandedHostContext();
    flushSync(() => {
      setExpandedTopInset(measureInset());
      setIsMobileExpanded(true);
    });
    requestAnimationFrame(() => {
      setExpandedTopInset(measureInset());
      focusComposerSurface();
    });
  }, [autoMobileExpandOnFocus, isAutoExpandViewport, isMobileExpanded, measureInset, focusComposerSurface, captureExpandedHostContext]);

  const expandCompose = useCallback(() => {
    if (!composeExpandOnFocus || isComposeExpanded) return;
    setIsComposeExpanded(true);
    requestAnimationFrame(() => {
      editorRef.current?.focus({ preventScroll: true });
    });
  }, [composeExpandOnFocus, isComposeExpanded]);

  const handleComposeTypingPointerDown = useCallback(
    (e) => {
      if (!composeExpandOnFocus || isComposeExpanded) return;
      const surfaceEl =
        mode === 'markdown' ? markdownTextareaRef.current : editorRef.current;
      if (!surfaceEl) return;
      const rect = surfaceEl.getBoundingClientRect();
      if (
        e.clientX < rect.left ||
        e.clientX > rect.right ||
        e.clientY < rect.top ||
        e.clientY > rect.bottom
      ) {
        return;
      }
      expandCompose();
    },
    [composeExpandOnFocus, isComposeExpanded, expandCompose, mode]
  );

  const handleComposeChromePointerDown = useCallback(
    (e) => {
      if (!composeExpandOnFocus) return;
      // Flag menus and toggles need normal pointer behavior (preventDefault suppresses clicks).
      if (
        e.target.closest(
          '.wfm-text-editor__flag-grouped, .gb__menu, .toggle-button, .wfm-text-editor__flag-toggle, .tooltip-container'
        )
      ) {
        return;
      }
      // Keep the typing surface focused when using toolbar / post controls.
      e.preventDefault();
    },
    [composeExpandOnFocus]
  );

  const handleToolbarComposePointerDown = useCallback(
    (e) => {
      if (!composeExpandOnFocus) return;
      if (e.target.closest('select, input[type="color"], input[type="file"], textarea')) return;
      if (
        e.target.closest(
          '.wfm-text-editor__file-dropdown, .reusable-dropdown, .wfm-text-editor__typography, .wfm-text-editor__typography-dropdown'
        )
      ) {
        return;
      }
      if (e.target.closest('button, .gb, .wfm-text-editor__tool-btn')) {
        e.preventDefault();
      }
    },
    [composeExpandOnFocus]
  );

  const handleToolbarVisibilityToggle = useCallback(() => {
    if (composeExpandOnFocus) {
      setIsComposeExpanded(true);
    }
    setIsToolbarVisible((v) => !v);
  }, [composeExpandOnFocus]);

  const handlePrimaryActionClick = useCallback(() => {
    if (!onPrimaryAction) return;
    flushSync(() => {
      flushDomToStateRef.current();
    });
    onPrimaryAction();
  }, [onPrimaryAction]);

  const handleSecondaryActionClick = useCallback(() => {
    if (autoMobileExpandOnFocus && isMobileExpanded) {
      flushSync(() => {
        setIsMobileExpanded(false);
        setExpandedVvBox(null);
        setExpandedTopInset(0);
      });
      document.body.style.removeProperty('overflow');
      document.documentElement.style.removeProperty('overflow');
    }
    if (composeExpandOnFocus) {
      setIsComposeExpanded(false);
      setIsToolbarVisible(false);
    }
    onSecondaryAction?.();
  }, [autoMobileExpandOnFocus, isMobileExpanded, composeExpandOnFocus, onSecondaryAction]);

  const handleComposeBlur = useCallback(
    (e) => {
      if (!composeExpandOnFocus || !isComposeExpanded) return;
      if (skipComposeCollapseRef.current || composeMoreMenuOpenRef.current || composeFlagMenuOpenRef.current) {
        return;
      }
      const related = e.relatedTarget;
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          if (
            skipComposeCollapseRef.current ||
            composeMoreMenuOpenRef.current ||
            composeFlagMenuOpenRef.current
          ) {
            return;
          }
          const root = editorShellRef.current;
          if (!root) return;
          if (root.querySelector('.gb__menu')) return;
          const active = document.activeElement;
          if (root.contains(active)) return;
          if (related && root.contains(related)) return;
          const isEmpty =
            mode === 'markdown'
              ? commentBodyIsEffectivelyEmpty(markdownContent)
              : isRichContentEmpty;
          if (!isEmpty) return;
          setIsComposeExpanded(false);
          setIsToolbarVisible(false);
        });
      });
    },
    [composeExpandOnFocus, isComposeExpanded, isRichContentEmpty, mode, markdownContent]
  );

  const handleTableResizeHandlePointerDown = useCallback(
    (e) => {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      e.preventDefault();
      e.stopPropagation();
      const table = activeTableResizeRef.current;
      const root = editorRef.current;
      if (!table || !root?.contains(table)) return;

      const startX = e.clientX;
      const startY = e.clientY;
      const startW = table.offsetWidth;
      const startH = table.offsetHeight;
      const maxW = root.clientWidth;

      const el = e.currentTarget;
      try {
        el.setPointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }

      const prevUserSelect = document.body.style.userSelect;
      document.body.style.userSelect = 'none';

      const onMove = (ev) => {
        const t = activeTableResizeRef.current;
        if (!t || !root.contains(t)) return;
        const dx = ev.clientX - startX;
        const dy = ev.clientY - startY;
        let w = Math.round(startW + dx);
        let h = Math.round(startH + dy);
        w = Math.max(80, Math.min(w, maxW));
        h = Math.max(48, h);
        t.style.width = `${w}px`;
        t.style.maxWidth = '100%';
        t.style.minHeight = `${h}px`;
        t.style.boxSizing = 'border-box';
        requestAnimationFrame(() => updateTableResizeUi());
      };

      let ended = false;
      const endDrag = () => {
        if (ended) return;
        ended = true;
        document.body.style.userSelect = prevUserSelect;
        document.removeEventListener('pointermove', onMove);
        document.removeEventListener('pointerup', onEnd);
        document.removeEventListener('pointercancel', onEnd);
        try {
          el.releasePointerCapture(e.pointerId);
        } catch {
          /* ignore */
        }

        const nextHtml = root.innerHTML || '';
        const nextMarkdown = htmlToMarkdown(nextHtml);
        setHtmlContent(nextHtml);
        setMarkdownContent(nextMarkdown);
        syncRichPlaceholderVisibility(nextHtml);
        updateOutput(nextHtml, nextMarkdown, 'rich');
        requestAnimationFrame(() => updateTableResizeUi());
      };

      const onEnd = () => endDrag();

      document.addEventListener('pointermove', onMove);
      document.addEventListener('pointerup', onEnd, { once: true });
      document.addEventListener('pointercancel', onEnd, { once: true });
    },
    [updateTableResizeUi, updateOutput, syncRichPlaceholderVisibility]
  );

  const runTableStructureMutation = useCallback(
    (action) => {
      const root = editorRef.current;
      if (!root || mode !== 'rich') return;
      root.focus();
      const ok = mutateTableStructure(root, action);
      if (!ok) return;
      const nextHtml = root.innerHTML || '';
      const nextMarkdown = htmlToMarkdown(nextHtml);
      setHtmlContent(nextHtml);
      setMarkdownContent(nextMarkdown);
      syncRichPlaceholderVisibility(nextHtml);
      updateOutput(nextHtml, nextMarkdown, 'rich');
      requestAnimationFrame(() => updateTableResizeUi());
    },
    [mode, updateOutput, updateTableResizeUi, syncRichPlaceholderVisibility]
  );

  flushDomToStateRef.current = () => {
    cancelIdle(richInputIdleRef);
    if (mode === 'rich' && editorRef.current) {
      const nextHtml = normalizeCommentBodyArtifacts(editorRef.current.innerHTML || '');
      const nextMarkdown = htmlToMarkdown(nextHtml);
      setHtmlContent(nextHtml);
      setMarkdownContent(nextMarkdown);
      syncRichPlaceholderVisibility(nextHtml);
      updateOutput(nextHtml, nextMarkdown, 'rich');
      return nextHtml;
    }
    if (mode === 'markdown' && markdownTextareaRef.current) {
      const nextMarkdown = markdownTextareaRef.current.value;
      const nextHtml = markdownToHtml(nextMarkdown);
      setMarkdownContent(nextMarkdown);
      setHtmlContent(nextHtml);
      updateOutput(nextHtml, nextMarkdown, 'markdown');
      return nextHtml;
    }
    return htmlContent;
  };

  useEffect(() => {
    if (!richHtmlSnapshotRef) return undefined;
    richHtmlSnapshotRef.current = () => flushDomToStateRef.current();
    return () => {
      richHtmlSnapshotRef.current = null;
    };
  }, [richHtmlSnapshotRef, mode, htmlContent]);

  useEffect(() => {
    updateOutput(htmlContent, markdownContent);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSpoiler, isAgeRestricted, isSensitivity, showContentFlags]);

  useEffect(() => {
    if (mode !== 'rich') {
      activeTableResizeRef.current = null;
      setTableResizeUi(null);
    }
  }, [mode]);

  useEffect(() => {
    if (mode !== 'rich') return undefined;
    const onDocPointerDown = (e) => {
      if (e.target?.closest?.('.wfm-text-editor__table-resize-handle')) return;
      if (e.target?.closest?.('.wfm-text-editor__table-props-floating')) return;
      const root = editorRef.current;
      if (root?.contains(e.target)) return;
      activeTableResizeRef.current = null;
      setTableResizeUi(null);
    };
    document.addEventListener('pointerdown', onDocPointerDown, true);
    return () => document.removeEventListener('pointerdown', onDocPointerDown, true);
  }, [mode]);

  useEffect(() => {
    if (!tableResizeUi || !editorRef.current) return undefined;
    const root = editorRef.current;
    const onReposition = () => updateTableResizeUi();
    root.addEventListener('scroll', onReposition, { passive: true });
    window.addEventListener('resize', onReposition, { passive: true });
    window.addEventListener('scroll', onReposition, { capture: true, passive: true });
    const vv = typeof window !== 'undefined' ? window.visualViewport : null;
    if (vv) {
      vv.addEventListener('resize', onReposition, { passive: true });
      vv.addEventListener('scroll', onReposition, { passive: true });
    }
    return () => {
      root.removeEventListener('scroll', onReposition);
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, { capture: true });
      if (vv) {
        vv.removeEventListener('resize', onReposition);
        vv.removeEventListener('scroll', onReposition);
      }
    };
  }, [tableResizeUi, updateTableResizeUi]);

  useEffect(() => {
    if (!isMobileExpanded) return undefined;
    const prevBodyOverflow = document.body.style.overflow;
    const prevHtmlOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevBodyOverflow;
      document.documentElement.style.overflow = prevHtmlOverflow;
      if (autoMobileExpandOnFocus) {
        document.body.style.removeProperty('overflow');
        document.documentElement.style.removeProperty('overflow');
      }
    };
  }, [isMobileExpanded, autoMobileExpandOnFocus]);

  useEffect(
    () => () => {
      if (!autoMobileExpandOnFocus) return;
      document.body.style.removeProperty('overflow');
      document.documentElement.style.removeProperty('overflow');
    },
    [autoMobileExpandOnFocus]
  );

  useLayoutEffect(() => {
    if (!autoMobileExpandOnFocus || !isAutoExpandViewport || isMobileExpanded) return undefined;
    const shell = editorShellRef.current;
    if (!shell?.contains(document.activeElement)) return undefined;
    triggerAutoMobileExpand();
    return undefined;
  }, [autoMobileExpandOnFocus, isAutoExpandViewport, isMobileExpanded, triggerAutoMobileExpand]);

  useLayoutEffect(() => {
    if (!isMobileExpanded) return undefined;
    if (focusComposerSurface()) return undefined;
    const t1 = window.setTimeout(() => focusComposerSurface(), 0);
    const t2 = window.setTimeout(() => focusComposerSurface(), 120);
    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
    };
  }, [isMobileExpanded, focusComposerSurface]);

  useLayoutEffect(() => {
    if (!isMobileExpanded) {
      setExpandedVvBox(null);
      setExpandedTopInset(0);
      expandedFromCreatePostRef.current = false;
      setExpandedFromCreatePost(false);
      return undefined;
    }
    const syncHeadingInset = () => setExpandedTopInset(measureInset());
    syncHeadingInset();

    const vv = typeof window !== 'undefined' ? window.visualViewport : null;
    const syncVv = () => {
      if (!vv) {
        setExpandedVvBox(null);
        return;
      }
      setExpandedVvBox({
        top: vv.offsetTop,
        left: vv.offsetLeft,
        width: vv.width,
        height: vv.height,
      });
    };
    syncVv();

    window.addEventListener('resize', syncHeadingInset, { passive: true });
    window.addEventListener('scroll', syncHeadingInset, { capture: true, passive: true });
    if (vv) {
      vv.addEventListener('resize', syncVv);
      vv.addEventListener('scroll', syncVv);
      vv.addEventListener('resize', syncHeadingInset);
      vv.addEventListener('scroll', syncHeadingInset);
    }
    return () => {
      window.removeEventListener('resize', syncHeadingInset);
      window.removeEventListener('scroll', syncHeadingInset, { capture: true });
      if (vv) {
        vv.removeEventListener('resize', syncVv);
        vv.removeEventListener('scroll', syncVv);
        vv.removeEventListener('resize', syncHeadingInset);
        vv.removeEventListener('scroll', syncHeadingInset);
      }
    };
  }, [isMobileExpanded, measureInset]);

  useEffect(() => {
    if (!isMobileExpanded) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (mention) {
        setMention(null);
        setMentionRows([]);
        setSelectedMentionEmail(null);
        e.preventDefault();
        return;
      }
      flushSync(() => {
        flushDomToStateRef.current();
        setIsMobileExpanded(false);
        setExpandedVvBox(null);
        setExpandedTopInset(0);
      });
      if (autoMobileExpandOnFocus) {
        document.body.style.removeProperty('overflow');
        document.documentElement.style.removeProperty('overflow');
      }
      e.preventDefault();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isMobileExpanded, mention, autoMobileExpandOnFocus]);

  useEffect(() => {
    const mq = window.matchMedia(TEXT_EDITOR_MOBILE_LAYOUT_MQ);
    const onChange = () => {
      if (!mq.matches) {
        flushSync(() => {
          flushDomToStateRef.current();
          setIsMobileExpanded(false);
        });
      }
    };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    const mq = window.matchMedia(TEXT_EDITOR_MOBILE_LAYOUT_MQ);
    const sync = () => setIsMentionMobileViewport(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  const closeModal = () => {
    setModalState({ isOpen: false, type: null, title: '', fields: [] });
    setModalValues({});
    setModalUploadFile(null);
    setModalUploadError('');
    setModalMediaBusy(false);
    setIsModalDragOver(false);
  };

  const openModal = (type, title, fields) => {
    const initialValues = fields.reduce((acc, field) => {
      acc[field.name] = field.defaultValue || '';
      return acc;
    }, {});
    setModalValues(initialValues);
    setModalUploadFile(null);
    setModalUploadError('');
    setModalMediaBusy(false);
    setIsModalDragOver(false);
    setModalState({ isOpen: true, type, title, fields });
  };

  const applyHtmlAndEmit = useCallback(
    (options = {}) => {
      cancelIdle(richInputIdleRef);
      const nextHtml = normalizeCommentBodyArtifacts(editorRef.current?.innerHTML || '');
      const nextMarkdown = htmlToMarkdown(nextHtml);
      const commit = () => {
        setHtmlContent(nextHtml);
        setMarkdownContent(nextMarkdown);
        syncRichPlaceholderVisibility(nextHtml);
        updateOutput(nextHtml, nextMarkdown);
      };
      if (options.flush) flushSync(commit);
      else commit();
    },
    [syncRichPlaceholderVisibility, updateOutput]
  );

  const applyRichTypography = (styleObj) => {
    const rangeSnapshot =
      pendingTypographyRangeRef.current?.cloneRange?.() ??
      lastNonEmptyRichSelectionRef.current?.cloneRange?.() ??
      savedRichSelectionRef.current?.cloneRange?.() ??
      null;
    if (!rangeSnapshot || !editorRef.current) return false;
    const applied = applyRichTypographyToRange(
      editorRef.current,
      styleObj,
      rangeSnapshot,
      savedRichSelectionRef
    );
    pendingTypographyRangeRef.current = null;
    if (applied) {
      applyHtmlAndEmit({ flush: true });
      editorRef.current.focus({ preventScroll: true });
    }
    return applied;
  };

  /** After insertHTML with video/figure, move caret into the trailing paragraph so typing isn’t blocked. */
  const focusRichEditorAfterMediaInsert = useCallback(() => {
    requestAnimationFrame(() => {
      const root = editorRef.current;
      if (!root) return;
      const mark = root.querySelector('p.wfm-text-editor__post-media-p');
      if (!mark) {
        root.focus();
        return;
      }
      mark.classList.remove('wfm-text-editor__post-media-p');
      const sel = window.getSelection();
      const range = document.createRange();
      range.setStart(mark, 0);
      range.collapse(true);
      sel.removeAllRanges();
      sel.addRange(range);
      root.focus();
    });
  }, []);

  const syncMentionFromRich = useCallback(() => {
    if (!mentionUserSearch || !editorRef.current) {
      setMention(null);
      setMentionRows([]);
      return;
    }
    const sel = window.getSelection();
    if (!sel?.rangeCount) {
      setMention(null);
      setMentionRows([]);
      return;
    }
    const root = editorRef.current;
    const r0 = sel.getRangeAt(0);
    if (!root.contains(r0.commonAncestorContainer)) {
      setMention(null);
      setMentionRows([]);
      return;
    }
    const prelude = getPreludeFromRichEditor(root, sel);
    const hit = findActiveMentionInPrelude(prelude);
    if (!hit) {
      setMention(null);
      setMentionRows([]);
      return;
    }
    const rect = r0.getBoundingClientRect();
    setMention({
      top: rect.bottom + 6,
      left: rect.left,
      query: hit.query,
      replaceStart: hit.atIndex,
      replaceEnd: prelude.length,
      source: 'rich',
    });
  }, [mentionUserSearch]);

  const syncMentionFromMarkdown = useCallback(() => {
    if (!mentionUserSearch || !markdownTextareaRef.current) {
      setMention(null);
      setMentionRows([]);
      return;
    }
    const ta = markdownTextareaRef.current;
    const prelude = ta.value.slice(0, ta.selectionStart);
    const hit = findActiveMentionInPrelude(prelude);
    if (!hit) {
      setMention(null);
      setMentionRows([]);
      return;
    }
    const rect = ta.getBoundingClientRect();
    const line = (prelude.match(/\n/g) || []).length;
    const lineHeight = Number.parseFloat(getComputedStyle(ta).lineHeight) || 22;
    const padTop = Number.parseFloat(getComputedStyle(ta).paddingTop) || 10;
    const top = Math.min(rect.top + padTop + (line + 1) * lineHeight, rect.bottom + 4);
    setMention({
      top,
      left: rect.left,
      query: hit.query,
      replaceStart: hit.atIndex,
      replaceEnd: prelude.length,
      source: 'markdown',
    });
  }, [mentionUserSearch]);

  const closeMentionMobileModal = useCallback(() => {
    setMention(null);
    setMentionRows([]);
    setSelectedMentionEmail(null);
    setMobileMentionQuery('');
    setMobileMentionList([]);
    setMobileMentionListLoading(false);
    if (mobileMentionSearchTimerRef.current) window.clearTimeout(mobileMentionSearchTimerRef.current);
    mobileMentionSearchAbortRef.current?.abort();
  }, []);

  const insertMentionChoice = useCallback(
    (user) => {
      if (!mention) return;
      const ins = `${formatMentionInsert(user)} `;
      if (mention.source === 'markdown' && markdownTextareaRef.current) {
        const ta = markdownTextareaRef.current;
        const v = ta.value;
        const { replaceStart, replaceEnd } = mention;
        const next = v.slice(0, replaceStart) + ins + v.slice(replaceEnd);
        const pos = replaceStart + ins.length;
        setMarkdownContent(next);
        const nextHtml = markdownToHtml(next);
        setHtmlContent(nextHtml);
        updateOutput(nextHtml, next, 'markdown');
        setMention(null);
        setMentionRows([]);
        setSelectedMentionEmail(null);
        window.requestAnimationFrame(() => {
          if (!markdownTextareaRef.current) return;
          const el = markdownTextareaRef.current;
          el.selectionStart = el.selectionEnd = pos;
          el.focus();
        });
        return;
      }
      if (mention.source === 'rich' && editorRef.current) {
        const root = editorRef.current;
        const a = resolveTextOffsetToPoint(root, mention.replaceStart);
        const b = resolveTextOffsetToPoint(root, mention.replaceEnd);
        if (!a || !b) {
          setMention(null);
          setMentionRows([]);
          setSelectedMentionEmail(null);
          return;
        }
        const range = document.createRange();
        const al = a.node.textContent?.length ?? 0;
        const bl = b.node.textContent?.length ?? 0;
        range.setStart(a.node, Math.min(a.offset, al));
        range.setEnd(b.node, Math.min(b.offset, bl));
        range.deleteContents();
        const tn = document.createTextNode(ins);
        range.insertNode(tn);
        const sel = window.getSelection();
        sel.removeAllRanges();
        const nr = document.createRange();
        nr.setStartAfter(tn);
        nr.collapse(true);
        sel.addRange(nr);
        applyHtmlAndEmit();
        setMention(null);
        setMentionRows([]);
        setSelectedMentionEmail(null);
      }
    },
    [mention, updateOutput, applyHtmlAndEmit]
  );

  const applyMentionMobileChoice = useCallback(() => {
    if (!selectedMentionEmail) return;
    const user = mobileMentionList.find((r) => String(r.email || '').trim() === String(selectedMentionEmail).trim());
    if (!user) return;
    insertMentionChoice(user);
  }, [selectedMentionEmail, mobileMentionList, insertMentionChoice]);

  useEffect(() => {
    setSelectedMentionEmail(null);
  }, [mention?.replaceStart, mention?.source]);

  useEffect(() => {
    if (!selectedMentionEmail) return;
    const list = isMentionMobileViewport ? mobileMentionList : mentionRows;
    if (!list.some((r) => String(r.email || '').trim() === String(selectedMentionEmail).trim())) {
      setSelectedMentionEmail(null);
    }
  }, [mentionRows, mobileMentionList, isMentionMobileViewport, selectedMentionEmail]);

  useEffect(() => {
    if (mention) return;
    setMobileMentionQuery('');
    setMobileMentionList([]);
    setMobileMentionListLoading(false);
    if (mobileMentionSearchTimerRef.current) window.clearTimeout(mobileMentionSearchTimerRef.current);
    mobileMentionSearchAbortRef.current?.abort();
  }, [mention]);

  useEffect(() => {
    if (!isMentionMobileViewport || !mentionUserSearch) return;
    const m = mentionRef.current;
    if (!m) return;
    setMobileMentionQuery(String(m.query || ''));
  }, [mention?.replaceStart, mention?.source, isMentionMobileViewport, mentionUserSearch]);

  /** If the user keeps typing in the main editor, mirror @-query into the modal field when the modal search box is not focused. */
  useEffect(() => {
    if (!isMentionMobileModalOpen || !mention) return;
    const el = mobileMentionSearchRef.current;
    if (el && document.activeElement === el) return;
    setMobileMentionQuery(String(mention.query || ''));
  }, [mention, isMentionMobileModalOpen]);

  useLayoutEffect(() => {
    if (!isMentionMobileModalOpen) return;
    const focusSearch = () => {
      editorRef.current?.blur();
      markdownTextareaRef.current?.blur();
      const el = mobileMentionSearchRef.current;
      if (!el) return;
      el.focus();
      try {
        const len = el.value.length;
        el.setSelectionRange(len, len);
      } catch {
        /* ignore */
      }
    };
    focusSearch();
    const t1 = window.setTimeout(focusSearch, 50);
    const t2 = window.setTimeout(focusSearch, 200);
    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
    };
  }, [isMentionMobileModalOpen, mention?.replaceStart, mention?.source]);

  useEffect(() => {
    if (!mention || !mentionUserSearch) return undefined;
    if (isMentionMobileViewport) return undefined;
    const q = String(mention.query || '');
    if (q.length < 2) {
      setMentionRows([]);
      setMentionLoading(false);
      return undefined;
    }
    if (mentionTimerRef.current) window.clearTimeout(mentionTimerRef.current);
    mentionAbortRef.current?.abort();
    mentionTimerRef.current = window.setTimeout(() => {
      const ac = new AbortController();
      mentionAbortRef.current = ac;
      setMentionLoading(true);
      Promise.resolve(mentionUserSearch(q, ac.signal))
        .then((rows) => {
          if (ac.signal.aborted) return;
          setMentionRows(Array.isArray(rows) ? rows : []);
          setMentionHi(0);
        })
        .catch(() => {
          if (!ac.signal.aborted) setMentionRows([]);
        })
        .finally(() => {
          if (!ac.signal.aborted) setMentionLoading(false);
        });
    }, 220);
    return () => {
      if (mentionTimerRef.current) window.clearTimeout(mentionTimerRef.current);
      mentionAbortRef.current?.abort();
    };
  }, [mention, mentionUserSearch, isMentionMobileViewport]);

  useEffect(() => {
    if (!mention || !mentionUserSearch || !isMentionMobileViewport) return undefined;
    const q = String(mobileMentionQuery || '').trim();
    if (q.length < 2) {
      setMobileMentionList([]);
      setMobileMentionListLoading(false);
      if (mobileMentionSearchTimerRef.current) window.clearTimeout(mobileMentionSearchTimerRef.current);
      mobileMentionSearchAbortRef.current?.abort();
      return undefined;
    }
    if (mobileMentionSearchTimerRef.current) window.clearTimeout(mobileMentionSearchTimerRef.current);
    mobileMentionSearchAbortRef.current?.abort();
    mobileMentionSearchTimerRef.current = window.setTimeout(() => {
      const ac = new AbortController();
      mobileMentionSearchAbortRef.current = ac;
      setMobileMentionListLoading(true);
      Promise.resolve(mentionUserSearch(q, ac.signal))
        .then((rows) => {
          if (ac.signal.aborted) return;
          setMobileMentionList(Array.isArray(rows) ? rows : []);
        })
        .catch(() => {
          if (!ac.signal.aborted) setMobileMentionList([]);
        })
        .finally(() => {
          if (!ac.signal.aborted) setMobileMentionListLoading(false);
        });
    }, 220);
    return () => {
      if (mobileMentionSearchTimerRef.current) window.clearTimeout(mobileMentionSearchTimerRef.current);
      mobileMentionSearchAbortRef.current?.abort();
    };
  }, [mention, mentionUserSearch, isMentionMobileViewport, mobileMentionQuery]);

  useEffect(() => {
    if (!mention) return undefined;
    const onDocDown = (e) => {
      const t = e.target;
      if (t?.closest?.('.wfm-mention-mobile-overlay, .wfm-text-editor__mention-mobile-modal')) return;
      if (mentionPopoverRef.current?.contains(t)) return;
      if (editorRef.current?.contains(t)) return;
      if (markdownTextareaRef.current?.contains(t)) return;
      setMention(null);
      setMentionRows([]);
      setSelectedMentionEmail(null);
    };
    document.addEventListener('pointerdown', onDocDown, true);
    return () => document.removeEventListener('pointerdown', onDocDown, true);
  }, [mention]);

  const handleMentionKeyDown = useCallback(
    (e) => {
      if (!mention) return;
      if (isMentionMobileViewport) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        setMention(null);
        setMentionRows([]);
        setSelectedMentionEmail(null);
        return;
      }
      if (!mentionRows.length) return;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setMentionHi((i) => Math.min(i + 1, mentionRows.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setMentionHi((i) => Math.max(i - 1, 0));
      } else if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        insertMentionChoice(mentionRows[mentionHi]);
      }
    },
    [mention, mentionRows, mentionHi, insertMentionChoice, isMentionMobileViewport]
  );

  const buildTableLayoutHtml = (rowsValue, colsValue) => {
    const rows = Math.max(1, Number.parseInt(rowsValue || '2', 10) || 2);
    const cols = Math.max(1, Number.parseInt(colsValue || '2', 10) || 2);

    const styleAttr = 'border-collapse:collapse;box-sizing:border-box;width:100%;max-width:100%';

    const headerCells = Array.from({ length: cols }, (_, index) => `<th>Header ${index + 1}</th>`).join('');
    const bodyRows = Array.from({ length: rows }, (_row, rowIndex) => {
      const rowCells = Array.from({ length: cols }, (_col, colIndex) => `<td>R${rowIndex + 1}C${colIndex + 1}</td>`).join('');
      return `<tr>${rowCells}</tr>`;
    }).join('');

    return `<table class="wfm-text-editor__table" style="${styleAttr}"><thead><tr>${headerCells}</tr></thead><tbody>${bodyRows}</tbody></table><p><br/></p>`;
  };

  const readFileAsDataUrl = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ''));
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(file);
    });

  const acceptUploadFile = (file) => {
    if (!file) return;
    const isImageModal = modalState.type === 'insertImage';
    const isVideoModal = modalState.type === 'insertVideo';
    const type = file.type || '';

    if (isImageModal && !type.startsWith('image/')) return;
    if (isVideoModal && !type.startsWith('video/')) return;
    setModalUploadFile(file);
    setModalUploadError('');
  };

  const handleModalFileInputChange = (event) => {
    const file = event.target.files?.[0];
    acceptUploadFile(file);
  };

  const handleModalDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsModalDragOver(false);
    const file = event.dataTransfer?.files?.[0];
    acceptUploadFile(file);
  };

  const handleModalSubmit = async (event) => {
    event.preventDefault();
    editorRef.current?.focus();

    if (modalState.type === 'createLink') {
      const href = modalValues.href?.trim();
      if (href) {
        document.execCommand('createLink', false, href);
      }
    } else if (modalState.type === 'insertImage') {
      setModalUploadError('');
      let src = '';
      if (modalUploadFile) {
        setModalMediaBusy(true);
        try {
          if (richMediaUpload) {
            src = await richMediaUpload(modalUploadFile);
          } else if (modalUploadFile.size > 48 * 1024) {
            setModalUploadError(
              'This image is too large to embed as text. Add a hosted image URL in the field above, or enable media upload.'
            );
            setModalMediaBusy(false);
            return;
          } else {
            src = await readFileAsDataUrl(modalUploadFile);
          }
        } catch (err) {
          setModalUploadError(err?.message || 'Upload failed');
          setModalMediaBusy(false);
          return;
        }
        setModalMediaBusy(false);
      } else {
        src = modalValues.src?.trim() || '';
      }
      const alt = modalValues.alt?.trim() || 'Inserted image';
      if (!src) return;
      const safeSrc = escapeHtml(normalizeAssetUrlForHtmlAttr(src));
      document.execCommand(
        'insertHTML',
        false,
        `<figure contenteditable="false"><img src="${safeSrc}" alt="${escapeHtml(alt)}" style="max-width:100%;height:auto;" /></figure><p class="wfm-text-editor__post-media-p"><br /></p>`
      );
      applyHtmlAndEmit();
      focusRichEditorAfterMediaInsert();
      closeModal();
      return;
    } else if (modalState.type === 'insertVideo') {
      setModalUploadError('');
      let src = '';
      if (modalUploadFile) {
        setModalMediaBusy(true);
        try {
          if (richMediaUpload) {
            src = await richMediaUpload(modalUploadFile);
          } else if (modalUploadFile.size > 48 * 1024) {
            setModalUploadError(
              'This video is too large to embed as text. Paste a video URL in the field above, or enable media upload.'
            );
            setModalMediaBusy(false);
            return;
          } else {
            src = await readFileAsDataUrl(modalUploadFile);
          }
        } catch (err) {
          setModalUploadError(err?.message || 'Upload failed');
          setModalMediaBusy(false);
          return;
        }
        setModalMediaBusy(false);
      } else {
        src = modalValues.src?.trim() || '';
      }
      if (!src) return;
      const safeSrc = escapeHtml(normalizeAssetUrlForHtmlAttr(src));
      document.execCommand(
        'insertHTML',
        false,
        `<div class="wfm-text-editor__video-wrap" contenteditable="false"><video controls src="${safeSrc}" style="width:100%;max-height:320px;">Your browser does not support video.</video></div><p class="wfm-text-editor__post-media-p"><br /></p>`
      );
      applyHtmlAndEmit();
      focusRichEditorAfterMediaInsert();
      closeModal();
      return;
    } else if (modalState.type === 'insertCode') {
      const snippet = modalValues.snippet || '';
      if (snippet.trim()) {
        document.execCommand(
          'insertHTML',
          false,
          `<pre><code>${escapeHtml(snippet)}</code></pre><p class="wfm-text-editor__post-media-p"><br /></p>`
        );
        applyHtmlAndEmit();
        focusRichEditorAfterMediaInsert();
        closeModal();
        return;
      }
    } else if (modalState.type === 'insertTable') {
      const tableHtml = buildTableLayoutHtml(modalValues.rows, modalValues.cols);
      document.execCommand('insertHTML', false, tableHtml);
    }

    applyHtmlAndEmit();
    closeModal();
  };

  const executeEditorCommand = (item) => {
    editorRef.current?.focus();

    if (item.command === 'decreaseFontSize') {
      document.execCommand('fontSize', false, '2');
    } else if (item.command === 'increaseFontSize') {
      document.execCommand('fontSize', false, '5');
    } else if (item.command === 'heading') {
      document.execCommand('formatBlock', false, 'h2');
    } else if (item.command === 'createLink') {
      openModal('createLink', 'Insert Link', [{ name: 'href', label: 'URL', placeholder: 'https://example.com', required: true }]);
      return;
    } else if (item.command === 'insertImage') {
      openModal('insertImage', 'Insert Image', [
        { name: 'src', label: 'Image URL (optional if uploading)', placeholder: 'https://example.com/image.png' },
        { name: 'alt', label: 'Alt Text', placeholder: 'Describe the image (optional)' }
      ]);
      return;
    } else if (item.command === 'insertVideo') {
      openModal('insertVideo', 'Insert Video', [
        { name: 'src', label: 'Video URL (optional if uploading)', placeholder: 'https://example.com/video.mp4' }
      ]);
      return;
    } else if (item.command === 'insertCode') {
      openModal('insertCode', 'Insert Code Snippet', [{ name: 'snippet', label: 'Code', placeholder: 'Paste code...', multiline: true, required: true }]);
      return;
    } else if (item.command === 'insertTable') {
      openModal('insertTable', 'Insert Table Layout', [
        { name: 'rows', label: 'Rows', placeholder: '2', defaultValue: '2', required: true },
        { name: 'cols', label: 'Columns', placeholder: '2', defaultValue: '2', required: true }
      ]);
      return;
    } else if (item.command === 'formatBlock') {
      document.execCommand('formatBlock', false, item.value || 'blockquote');
    } else {
      document.execCommand(item.command, false, null);
    }
    applyHtmlAndEmit();
  };

  const handleToolbarFontFamilySelect = useCallback(
    (option) => {
      const value = option?.value ?? '';
      setToolbarFontFamily(value);
      if (!value || value === 'inherit') return;
      applyRichTypography({ fontFamily: value });
    },
    [applyRichTypography]
  );

  const handleToolbarFontSizeSelect = useCallback(
    (option) => {
      const value = option?.value ?? '';
      setToolbarFontSize(value);
      setToolbarFontSizeDraft(formatEditorFontSizeInputValue(value));
      if (!value) return;
      applyRichTypography({ fontSize: value });
    },
    [applyRichTypography]
  );

  const commitToolbarFontSizeDraft = useCallback(
    (raw) => {
      const normalized = normalizeEditorFontSizeValue(raw);
      if (normalized === null) {
        setToolbarFontSizeDraft(formatEditorFontSizeInputValue(toolbarFontSize));
        return;
      }
      if (!normalized) {
        setToolbarFontSize('');
        setToolbarFontSizeDraft('');
        return;
      }
      setToolbarFontSize(normalized);
      setToolbarFontSizeDraft(formatEditorFontSizeInputValue(normalized));
      applyRichTypography({ fontSize: normalized });
    },
    [applyRichTypography, toolbarFontSize]
  );

  const handleToolbarFontSizeInputChange = (event) => {
    setToolbarFontSizeDraft(event.target.value);
  };

  const handleToolbarFontSizeInputKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      commitToolbarFontSizeDraft(event.currentTarget.value);
    }
  };

  const handleToolbarFontSizeInputBlur = (event) => {
    commitToolbarFontSizeDraft(event.target.value);
  };

  const handleToolbarTextColorChange = (event) => {
    const value = event.target.value;
    setToolbarTextColor(value);
    applyRichTypography({ color: value });
  };

  const handleTypographyMouseDownCapture = () => {
    captureRichEditorSelection(editorRef.current, savedRichSelectionRef, lastNonEmptyRichSelectionRef);
    const snap =
      lastNonEmptyRichSelectionRef.current?.cloneRange?.() ??
      savedRichSelectionRef.current?.cloneRange?.() ??
      null;
    pendingTypographyRangeRef.current = snap;
  };

  const flushRichInput = useCallback(() => {
    const nextHtml = normalizeCommentBodyArtifacts(editorRef.current?.innerHTML || '');
    if (nextHtml === htmlContentRef.current) return;
    const nextMarkdown = htmlToMarkdown(nextHtml);
    setHtmlContent(nextHtml);
    setMarkdownContent(nextMarkdown);
    syncRichPlaceholderVisibility(nextHtml);
    updateOutput(nextHtml, nextMarkdown, 'rich');
  }, [syncRichPlaceholderVisibility, updateOutput]);

  const handleRichInput = useCallback(() => {
    const root = editorRef.current;
    if (root) {
      const empty = !(root.textContent || '').replace(/\u00a0/g, ' ').trim();
      setIsRichContentEmpty((prev) => (prev === empty ? prev : empty));
    }
    if (mentionUserSearch) {
      window.requestAnimationFrame(() => syncMentionFromRich());
    }
    scheduleIdle(richInputIdleRef, RICH_INPUT_IDLE_MS, flushRichInput);
  }, [mentionUserSearch, syncMentionFromRich, flushRichInput]);

  useEffect(
    () => () => {
      cancelIdle(richInputIdleRef);
      cancelIdle(documentPageCountIdleRef);
    },
    []
  );

  const handleRichSurfaceKeyDown = useCallback(
    (e) => {
      if (
        submitOnModifierEnter &&
        e.key === 'Enter' &&
        (e.ctrlKey || e.metaKey) &&
        onPrimaryAction &&
        !primaryActionDisabled
      ) {
        e.preventDefault();
        onPrimaryAction();
        return;
      }
      if (mention && !isMentionMobileViewport) {
        handleMentionKeyDown(e);
        return;
      }
      const root = editorRef.current;
      if (root && tryRemoveCodeBlockOnBoundaryKey(root, e)) {
        e.stopPropagation();
        handleRichInput();
        return;
      }
      if (e.key === 'Tab' && !e.altKey && !e.ctrlKey && !e.metaKey) {
        if (root && navigateTableTab(root, e.shiftKey)) {
          e.preventDefault();
          e.stopPropagation();
          handleRichInput();
        }
      }
    },
    [mention, isMentionMobileViewport, handleMentionKeyDown, handleRichInput, submitOnModifierEnter, onPrimaryAction, primaryActionDisabled]
  );

  const handleMarkdownChange = (event) => {
    const nextMarkdown = event.target.value;
    const nextHtml = markdownToHtml(nextMarkdown);
    setMarkdownContent(nextMarkdown);
    setHtmlContent(nextHtml);
    updateOutput(nextHtml, nextMarkdown, 'markdown');
    window.requestAnimationFrame(() => syncMentionFromMarkdown());
  };

  const handleModeChange = (nextMode) => {
    if (lockEditorMode) return;
    if (nextMode === mode) return;
    skipComposeCollapseRef.current = true;
    if (nextMode === 'markdown') {
      const nextHtml = editorRef.current?.innerHTML || htmlContent;
      const nextMarkdown = htmlToMarkdown(nextHtml);
      setHtmlContent(nextHtml);
      setMarkdownContent(nextMarkdown);
      updateOutput(nextHtml, nextMarkdown, nextMode);
    } else {
      const nextHtml = markdownToHtml(markdownContent);
      setHtmlContent(nextHtml);
      updateOutput(nextHtml, markdownContent, nextMode);
    }
    if (composeExpandOnFocus) {
      setIsComposeExpanded(true);
    }
    setMode(nextMode);
    window.requestAnimationFrame(() => {
      skipComposeCollapseRef.current = false;
    });
  };

  const handleDocumentExport = useCallback(
    (format) => {
      if (mode === 'rich') {
        applyHtmlAndEmit();
      }
      const rawHtml =
        mode === 'rich'
          ? normalizeCommentBodyArtifacts(editorRef.current?.innerHTML || htmlContent || '')
          : markdownToHtml(markdownContent || '');
      const bodyHtml = sanitizeHtml(rawHtml);
      if (commentBodyIsEffectivelyEmpty(bodyHtml)) return;
      void runTextEditorExport(format, {
        bodyHtml,
        baseName: exportBaseFileName,
        title: exportBaseFileName,
        liveRoot: mode === 'rich' ? editorRef.current : null,
        pageOrientation,
        pageLayoutMode,
        printMarginPreset,
        customPrintMarginsIn,
        printPageNumbersEnabled,
      }).catch(() => {
        window.alert('Export failed. Try again or export as HTML.');
      });
    },
    [
      mode,
      htmlContent,
      markdownContent,
      exportBaseFileName,
      applyHtmlAndEmit,
      pageOrientation,
      pageLayoutMode,
      printMarginPreset,
      customPrintMarginsIn,
      printPageNumbersEnabled,
    ]
  );

  const collectDocumentBodyHtml = useCallback(() => {
    if (mode === 'rich') {
      applyHtmlAndEmit();
    }
    const rawHtml =
      mode === 'rich'
        ? normalizeCommentBodyArtifacts(editorRef.current?.innerHTML || htmlContent || '')
        : markdownToHtml(markdownContent || '');
    return sanitizeHtml(rawHtml);
  }, [mode, htmlContent, markdownContent, applyHtmlAndEmit]);

  const resetDocumentEditor = useCallback(() => {
    const defaults = getDefaultDocumentPageSettings();
    pageSettingsChangeSkipRef.current = true;
    setPageOrientation(defaults.pageOrientation);
    setPageLayoutMode(defaults.pageLayoutMode);
    setPrintMarginPreset(defaults.printMarginPreset);
    setCustomPrintMarginsIn({ ...defaults.customPrintMarginsIn });
    setPrintPageNumbersEnabled(defaults.printPageNumbersEnabled);
    setHtmlContent('');
    setMarkdownContent('');
    setIsRichContentEmpty(true);
    if (editorRef.current) {
      editorRef.current.innerHTML = '';
    }
    updateOutput('', '', 'rich');
  }, [updateOutput]);

  const loadLocalDocumentIntoEditor = useCallback(
    (parsed) => {
      if (!parsed || parsed.mode !== 'rich') {
        window.alert('Only rich documents can be opened in this editor.');
        return;
      }
      const settings = parsed.documentPageSettings || getDefaultDocumentPageSettings();
      pageSettingsChangeSkipRef.current = true;
      setPageOrientation(settings.pageOrientation);
      setPageLayoutMode(settings.pageLayoutMode);
      setPrintMarginPreset(settings.printMarginPreset);
      setCustomPrintMarginsIn({ ...settings.customPrintMarginsIn });
      setPrintPageNumbersEnabled(settings.printPageNumbersEnabled);
      const html = parsed.html || '';
      setHtmlContent(html);
      setIsRichContentEmpty(commentBodyIsEffectivelyEmpty(html));
      if (editorRef.current) {
        editorRef.current.innerHTML = html;
      }
      updateOutput(html, '', 'rich');
      onFileMenuDocumentLoaded?.(parsed);
    },
    [onFileMenuDocumentLoaded, updateOutput]
  );

  const notifyDocumentActionBlocked = useCallback((actionLabel) => {
    window.alert(`Add some content to the document before using ${actionLabel}.`);
  }, []);

  const handleFileMenuAction = useCallback(
    (action, detail) => {
      if (action === 'open-file') {
        const file = detail?.file;
        if (!file) return;
        const reader = new FileReader();
        reader.onload = () => {
          try {
            const parsed = parseLocalDocumentFile(reader.result);
            loadLocalDocumentIntoEditor(parsed);
          } catch (err) {
            window.alert(err?.message || 'Could not open this file.');
          }
        };
        reader.onerror = () => window.alert('Could not read this file.');
        reader.readAsText(file);
        return;
      }

      switch (action) {
        case 'new':
          resetDocumentEditor();
          onFileMenuNew?.();
          break;
        case 'save':
          if (fileMenuUserLoggedIn && fileMenuAutosaveEnabled) return;
          if (mode === 'rich') applyHtmlAndEmit();
          onFileMenuSave?.();
          break;
        case 'save-as-local': {
          if (mode === 'rich') applyHtmlAndEmit();
          const bodyHtml = collectDocumentBodyHtml();
          if (commentBodyIsEffectivelyEmpty(bodyHtml)) {
            notifyDocumentActionBlocked('Save to device');
            return;
          }
          const buildPayload = () =>
            buildLocalDocumentFilePayload({
              html: bodyHtml,
              documentPageSettings: {
                pageOrientation,
                pageLayoutMode,
                printMarginPreset,
                customPrintMarginsIn: { ...customPrintMarginsIn },
                printPageNumbersEnabled,
              },
              title: exportBaseFileName,
              mode: 'rich',
            });
          promptSaveLocalDocumentPickerFirst(buildPayload, exportBaseFileName)
            .then((saved) => {
              if (saved) onFileMenuSaveAsLocal?.();
            })
            .catch(() => {
              window.alert('Could not save this file. Try again or use Chrome or Edge for Save As.');
            });
          break;
        }
        case 'save-as-storagebox':
          if (mode === 'rich') applyHtmlAndEmit();
          onFileMenuSaveAsStorageBox?.();
          break;
        case 'export-html':
          handleDocumentExport('html');
          break;
        case 'export-word':
          handleDocumentExport('word');
          break;
        case 'export-docx':
          handleDocumentExport('docx');
          break;
        case 'export-pdf':
          handleDocumentExport('pdf');
          break;
        case 'print': {
          if (mode === 'rich') applyHtmlAndEmit();
          const bodyHtml = collectDocumentBodyHtml();
          if (commentBodyIsEffectivelyEmpty(bodyHtml)) {
            notifyDocumentActionBlocked('Print');
            return;
          }
          runTextEditorPrint({
            bodyHtml,
            title: exportBaseFileName,
            liveRoot: editorRef.current,
            pageOrientation,
            pageLayoutMode,
            printMarginPreset,
            customPrintMarginsIn,
            printPageNumbersEnabled,
          });
          break;
        }
        case 'exit':
          onFileMenuExit?.();
          break;
        default:
          break;
      }
    },
    [
      resetDocumentEditor,
      onFileMenuNew,
      fileMenuUserLoggedIn,
      fileMenuAutosaveEnabled,
      mode,
      applyHtmlAndEmit,
      onFileMenuSave,
      onFileMenuSaveAsStorageBox,
      onFileMenuSaveAsLocal,
      collectDocumentBodyHtml,
      pageOrientation,
      pageLayoutMode,
      printMarginPreset,
      customPrintMarginsIn,
      printPageNumbersEnabled,
      exportBaseFileName,
      handleDocumentExport,
      loadLocalDocumentIntoEditor,
      onFileMenuExit,
      notifyDocumentActionBlocked,
    ]
  );

  const fileMenuOptions = useMemo(() => {
    if (!documentExport || mode !== 'rich') return [];
    const saveDisabled = Boolean(fileMenuUserLoggedIn && fileMenuAutosaveEnabled);
    return [
      { value: 'new', label: 'New', icon: <FiFile aria-hidden /> },
      { value: 'open', label: 'Open…', icon: <FiFolderPlus aria-hidden /> },
      {
        value: 'save',
        label: 'Save',
        icon: <FiSave aria-hidden />,
        disabled: saveDisabled,
      },
      {
        value: 'save-as',
        label: 'Save as…',
        icon: <MdOutlineSaveAs aria-hidden />,
        submenu: [
          ...(fileMenuUserLoggedIn && onFileMenuSaveAsStorageBox
            ? [
                {
                  value: 'save-as-storagebox',
                  label: 'Storage Box',
                  icon: <TbCloudUpload aria-hidden />,
                },
              ]
            : []),
          {
            value: 'save-as-local',
            label: 'Save to device… (choose location)',
            icon: <TbDeviceFloppy aria-hidden />,
          },
        ],
      },
      {
        value: 'export',
        label: 'Export',
        icon: <MdOutlineFileUpload aria-hidden />,
        submenu: [
          {
            value: 'export-html',
            label: 'Export as HTML',
            icon: <TbFileTypeHtml aria-hidden />,
          },
          {
            value: 'export-word',
            label: 'Export as Word (.doc)',
            icon: <TbFileTypeDoc aria-hidden />,
          },
          {
            value: 'export-docx',
            label: 'Export as Word (.docx)',
            icon: <TbFileTypeDocx aria-hidden />,
          },
          {
            value: 'export-pdf',
            label: 'Export as PDF',
            icon: <TbFileTypePdf aria-hidden />,
          },
        ],
      },
      { value: 'print', label: 'Print…', icon: <FiPrinter aria-hidden /> },
      { value: 'exit', label: 'Exit', icon: <FiLogOut aria-hidden /> },
    ];
  }, [
    documentExport,
    mode,
    fileMenuUserLoggedIn,
    fileMenuAutosaveEnabled,
    onFileMenuSaveAsStorageBox,
  ]);

  const isDocumentPaginatedLayout =
    documentExport &&
    mode === 'rich' &&
    pageLayoutMode === TEXT_EDITOR_PAGE_LAYOUT_MODE.PAGINATED;

  const documentCanvasMinHeight = useMemo(() => {
    if (!isDocumentPaginatedLayout) return undefined;
    const sheetHeight = getTextEditorPageSheetOuterHeightPx(pageOrientation);
    return (
      documentPageCount * sheetHeight +
      Math.max(0, documentPageCount - 1) * TEXT_EDITOR_PAGE_SHEET_GAP_PX
    );
  }, [isDocumentPaginatedLayout, documentPageCount, pageOrientation]);

  const documentSheetWidthPx = useMemo(
    () => getTextEditorPageSheetOuterWidthPx(pageOrientation),
    [pageOrientation]
  );

  const documentSheetHeightPx = useMemo(
    () => getTextEditorPageSheetOuterHeightPx(pageOrientation),
    [pageOrientation]
  );

  const documentPageCanvasLogicalHeight = useMemo(() => {
    if (!documentExport || mode !== 'rich') return documentSheetHeightPx;
    if (isDocumentPaginatedLayout && documentCanvasMinHeight) {
      return documentCanvasMinHeight;
    }
    return documentSheetHeightPx;
  }, [
    documentExport,
    mode,
    isDocumentPaginatedLayout,
    documentCanvasMinHeight,
    documentSheetHeightPx,
  ]);

  useLayoutEffect(() => {
    if (!documentExport || mode !== 'rich') {
      setPagePreviewScale(1);
      return undefined;
    }
    if (isMentionMobileViewport) {
      setPagePreviewScale(1);
      return undefined;
    }
    const wrap = surfaceWrapRef.current;
    if (!wrap) return undefined;

    const syncScale = () => {
      scheduleOnNextFrame(pagePreviewScaleFrameRef, () => {
        const wrapEl = surfaceWrapRef.current;
        if (!wrapEl) return;
        const next = getTextEditorPagePreviewScale(
          wrapEl.clientWidth,
          wrapEl.clientHeight,
          pageOrientation,
          true,
          false
        );
        setPagePreviewScale((prev) => (Math.abs(prev - next) < 0.002 ? prev : next));
      });
    };

    syncScale();
    let resizeObserver;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(syncScale);
      resizeObserver.observe(wrap);
    }
    window.addEventListener('resize', syncScale, { passive: true });
    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener('resize', syncScale);
      if (pagePreviewScaleFrameRef.current != null) {
        cancelAnimationFrame(pagePreviewScaleFrameRef.current);
        pagePreviewScaleFrameRef.current = null;
      }
    };
  }, [
    documentExport,
    mode,
    pageOrientation,
    isMobileExpanded,
    documentPageCanvasLogicalHeight,
    isMentionMobileViewport,
  ]);

  const useMobileReadableDocumentLayout =
    isMentionMobileViewport && documentExport && mode === 'rich';

  const pagePreviewHostWidthPx = documentSheetWidthPx * pagePreviewScale;
  const pagePreviewHostMinHeightPx = documentPageCanvasLogicalHeight * pagePreviewScale;
  const pagePreviewHostWidthStyle = pagePreviewHostWidthPx;

  const pagePropertiesMenuItems = useMemo(() => {
    if (!documentExport) return [];
    return [
      { type: 'label', id: 'page-prop-orientation-label', text: 'Orientation' },
      {
        id: 'page-orient-portrait',
        text: 'Portrait (A4)',
        icon: <TbCropPortrait aria-hidden />,
        selected: pageOrientation === TEXT_EDITOR_PAGE_ORIENTATION.PORTRAIT,
        onClick: () => setPageOrientation(TEXT_EDITOR_PAGE_ORIENTATION.PORTRAIT),
      },
      {
        id: 'page-orient-landscape',
        text: 'Landscape (A4)',
        icon: <TbCropLandscape aria-hidden />,
        selected: pageOrientation === TEXT_EDITOR_PAGE_ORIENTATION.LANDSCAPE,
        onClick: () => setPageOrientation(TEXT_EDITOR_PAGE_ORIENTATION.LANDSCAPE),
      },
      { type: 'separator', id: 'page-prop-sep-layout' },
      { type: 'label', id: 'page-prop-layout-label', text: 'Layout' },
      {
        id: 'page-layout-paginated',
        text: 'Paginated',
        icon: <TbLayoutList aria-hidden />,
        selected: pageLayoutMode === TEXT_EDITOR_PAGE_LAYOUT_MODE.PAGINATED,
        onClick: () => setPageLayoutMode(TEXT_EDITOR_PAGE_LAYOUT_MODE.PAGINATED),
      },
      {
        id: 'page-layout-continuous',
        text: 'Continuous',
        icon: <TbLayoutRows aria-hidden />,
        selected: pageLayoutMode === TEXT_EDITOR_PAGE_LAYOUT_MODE.CONTINUOUS,
        onClick: () => setPageLayoutMode(TEXT_EDITOR_PAGE_LAYOUT_MODE.CONTINUOUS),
      },
      { type: 'separator', id: 'page-prop-sep-print-numbers' },
      {
        type: 'toggle',
        id: 'print-page-numbers',
        text: 'Page numbers when printing',
        icon: <TbListNumbers aria-hidden />,
        checked: printPageNumbersEnabled,
        disabled: pageLayoutMode !== TEXT_EDITOR_PAGE_LAYOUT_MODE.PAGINATED,
        className:
          pageLayoutMode !== TEXT_EDITOR_PAGE_LAYOUT_MODE.PAGINATED
            ? 'gb__menu-item-wrap--disabled'
            : undefined,
        onToggle: setPrintPageNumbersEnabled,
        ariaLabel: 'Page numbers when printing',
      },
      { type: 'separator', id: 'page-prop-sep-margins' },
      { type: 'label', id: 'page-prop-margins-label', text: 'Print margins' },
      {
        id: 'margin-default',
        text: 'Default (1")',
        selected: printMarginPreset === TEXT_EDITOR_PRINT_MARGIN_PRESET.DEFAULT,
        onClick: () => setPrintMarginPreset(TEXT_EDITOR_PRINT_MARGIN_PRESET.DEFAULT),
      },
      {
        id: 'margin-narrow',
        text: 'Narrow (0.5")',
        selected: printMarginPreset === TEXT_EDITOR_PRINT_MARGIN_PRESET.NARROW,
        onClick: () => setPrintMarginPreset(TEXT_EDITOR_PRINT_MARGIN_PRESET.NARROW),
      },
      {
        id: 'margin-moderate',
        text: 'Moderate (0.75")',
        selected: printMarginPreset === TEXT_EDITOR_PRINT_MARGIN_PRESET.MODERATE,
        onClick: () => setPrintMarginPreset(TEXT_EDITOR_PRINT_MARGIN_PRESET.MODERATE),
      },
      {
        id: 'margin-wide',
        text: 'Wide (1.25")',
        selected: printMarginPreset === TEXT_EDITOR_PRINT_MARGIN_PRESET.WIDE,
        onClick: () => setPrintMarginPreset(TEXT_EDITOR_PRINT_MARGIN_PRESET.WIDE),
      },
      {
        id: 'margin-custom-open',
        text: 'Custom margins…',
        selected: printMarginPreset === TEXT_EDITOR_PRINT_MARGIN_PRESET.CUSTOM,
        onClick: () => setIsPrintMarginsModalOpen(true),
      },
    ];
  }, [documentExport, pageOrientation, pageLayoutMode, printMarginPreset, printPageNumbersEnabled]);

  const pagePropertiesTriggerTitle = useMemo(() => {
    const orientationLabel =
      pageOrientation === TEXT_EDITOR_PAGE_ORIENTATION.LANDSCAPE ? 'Landscape' : 'Portrait';
    const layoutLabel =
      pageLayoutMode === TEXT_EDITOR_PAGE_LAYOUT_MODE.CONTINUOUS ? 'Continuous' : 'Paginated';
    const numbersLabel =
      pageLayoutMode === TEXT_EDITOR_PAGE_LAYOUT_MODE.PAGINATED
        ? resolvedPrintPageNumbers
          ? 'page numbers on'
          : 'page numbers off'
        : 'no page numbers';
    return `Page: ${orientationLabel} (A4), ${layoutLabel}, ${numbersLabel}. ${formatPrintMarginsSummary(resolvedPrintMargins)}`;
  }, [pageOrientation, pageLayoutMode, resolvedPrintMargins, resolvedPrintPageNumbers]);

  const toolbarMoreItems = useMemo(() => {
    const items = [];
    if (!lockEditorMode) {
      items.push({
        id: 'more-markdown',
        text: 'Switch to Markdown',
        icon: <FaMarkdown aria-hidden />,
        disabled: mode === 'markdown',
        onClick: () => handleModeChange('markdown'),
      });
    }
    return items;
  }, [lockEditorMode, mode, handleModeChange]);

  const markdownHelp = useMemo(() => {
    const rows = [
      { syntax: '# Heading 1', example: '# Product Update' },
      { syntax: '## Heading 2', example: '## Highlights' },
      { syntax: '**bold**', example: '**Important note**' },
      { syntax: '*italic*', example: '*Side comment*' },
      { syntax: '__underline__', example: '__Underline text__' },
      { syntax: '<sub>text</sub> (X1)', example: '<sub>CO2</sub>' },
      { syntax: '<sup>text</sup> (X2)', example: '<sup>2</sup> power' },
      { syntax: '<span style="font-size:1.15em">...</span> (A+)', example: '<span style="font-size:1.15em">Bigger text</span>' },
      { syntax: '<span style="font-size:0.9em">...</span> (A-)', example: '<span style="font-size:0.9em">Smaller text</span>' },
      { syntax: '<span style="font-family:...">...</span>', example: '<span style="font-family:Inter, sans-serif">Inter text</span>' },
      { syntax: '<span style="font-size:18px">...</span>', example: '<span style="font-size:18px">Sized text</span>' },
      { syntax: '<span style="color:#b87333">...</span>', example: '<span style="color:#b87333">Colored text</span>' },
      { syntax: '[label](https://url)', example: '[Docs](https://example.com)' },
      { syntax: '![alt text](https://image-url)', example: '![Architecture](https://example.com/arch.png)' },
      { syntax: '<video controls src="..."></video>', example: '<video controls src="https://example.com/clip.mp4"></video>' },
      { syntax: '> quote', example: '> This is a quote' },
      { syntax: '- item (Ordered list in toolbar)', example: '- first bullet' },
      { syntax: '1. item', example: '1. first step' },
      { syntax: '`inline code`', example: '`const value = 1;`' },
      { syntax: '```code block```', example: '```js\nconsole.log("hello");\n```' },
      { syntax: '| table | row |', example: '| Name | Role |\n|---|---|\n| Raj | Admin |' },
    ];
    if (showContentFlags) {
      rows.push({
        syntax: 'Spoiler / 18+ / Sensitivity',
        example: 'Use the Flag menu toggles (metadata, not markdown syntax)',
      });
    }
    return rows;
  }, [showContentFlags]);

  const mobileExpandToggle = (
    <ButtonPill
      type="button"
      shape="pill"
      size="sm"
      variant="outline"
      className={classNames('wfm-text-editor__tool-btn', 'wfm-text-editor__expand-btn')}
      title={isMobileExpanded ? 'Collapse editor' : 'Expand editor space'}
      aria-label={isMobileExpanded ? 'Collapse editor' : 'Expand editor space'}
      aria-expanded={isMobileExpanded}
      onClick={() => {
        if (isMobileExpanded) {
          flushSync(() => {
            flushDomToStateRef.current();
            setIsMobileExpanded(false);
            setExpandedVvBox(null);
            setExpandedTopInset(0);
            expandedFromCreatePostRef.current = false;
            setExpandedFromCreatePost(false);
          });
          if (autoMobileExpandOnFocus) {
            document.body.style.removeProperty('overflow');
            document.documentElement.style.removeProperty('overflow');
          }
        } else {
          captureExpandedHostContext();
          flushSync(() => {
            setExpandedTopInset(measureInset());
            setIsMobileExpanded(true);
          });
          requestAnimationFrame(() => {
            setExpandedTopInset(measureInset());
            editorRef.current?.scrollTo?.({ top: 0, left: 0 });
            if (markdownTextareaRef.current) {
              markdownTextareaRef.current.scrollTop = 0;
            }
          });
        }
      }}
      iconStart={isMobileExpanded ? <MdCloseFullscreen /> : <MdOpenInFull />}
    >
      {null}
    </ButtonPill>
  );

  const tableStructureGroupedItems = useMemo(
    () => [
      {
        id: 'table-add-row',
        text: 'Add row below',
        onClick: () => runTableStructureMutation('addRowBelow')
      },
      {
        id: 'table-add-col',
        text: 'Add column right',
        onClick: () => runTableStructureMutation('addColumnRight')
      },
      {
        id: 'table-del-row',
        text: 'Delete row',
        onClick: () => runTableStructureMutation('deleteRow')
      },
      {
        id: 'table-del-col',
        text: 'Delete column',
        onClick: () => runTableStructureMutation('deleteColumn')
      }
    ],
    [runTableStructureMutation]
  );

  const handleFlagMenuOpenChange = useCallback((open) => {
    composeFlagMenuOpenRef.current = open;
  }, []);

  const contentFlagGrouped = showContentFlags && controlsVisibility.flag ? (
    <ContentFlagMenu
      isSpoiler={isSpoiler}
      isAgeRestricted={isAgeRestricted}
      isSensitivity={isSensitivity}
      onSpoilerChange={setIsSpoiler}
      onAgeRestrictedChange={setIsAgeRestricted}
      onSensitivityChange={setIsSensitivity}
      onOpenChange={composeExpandOnFocus ? handleFlagMenuOpenChange : undefined}
    />
  ) : null;

  const expandedViewportStyle = useMemo(() => {
    if (!isMobileExpanded) return undefined;
    const headingInset = expandedTopInset;
    const cssVars = { '--wfm-expanded-top-inset': `${headingInset}px` };
    if (expandedVvBox) {
      const height = Math.max(160, expandedVvBox.height - headingInset);
      return {
        ...cssVars,
        top: expandedVvBox.top + headingInset,
        left: expandedVvBox.left,
        width: expandedVvBox.width,
        height,
        maxHeight: height,
        right: 'auto',
        bottom: 'auto',
      };
    }
    return {
      ...cssVars,
      top: headingInset,
      height: `calc(100dvh - ${headingInset}px)`,
      maxHeight: `calc(100dvh - ${headingInset}px)`,
      right: 0,
      bottom: 'auto',
    };
  }, [isMobileExpanded, expandedVvBox, expandedTopInset]);

  const editorShellStyle = useMemo(() => {
    const marginVars =
      documentExport && mode === 'rich'
        ? getTextEditorPrintMarginStyleProperties(resolvedPrintMargins)
        : null;
    if (!expandedViewportStyle && !marginVars) return undefined;
    return { ...(expandedViewportStyle || {}), ...(marginVars || {}) };
  }, [documentExport, mode, expandedViewportStyle, resolvedPrintMargins]);

  const handlePrintMarginsCustomApply = useCallback((marginsIn) => {
    setCustomPrintMarginsIn(normalizeCustomPrintMarginsIn(marginsIn));
    setPrintMarginPreset(TEXT_EDITOR_PRINT_MARGIN_PRESET.CUSTOM);
  }, []);

  const bottomBarPrimaryAction =
    controlsVisibility.comment && primaryActionText && onPrimaryAction ? (
      <ButtonPill
        type="button"
        shape="pill"
        size="xs"
        onClick={handlePrimaryActionClick}
        onMouseDown={composeExpandOnFocus ? handleComposeChromePointerDown : undefined}
        disabled={primaryActionDisabled}
        className={primaryActionClassName || undefined}
      >
        {primaryActionText}
      </ButtonPill>
    ) : null;

  const showComposeCancel =
    controlsVisibility.cancel &&
    composeExpandOnFocus &&
    isComposeExpanded &&
    typeof onSecondaryAction === 'function';
  const bottomBarSecondaryAction =
    showComposeCancel || (controlsVisibility.otherButtons && secondaryActionText && onSecondaryAction) ? (
      <NoOutlineTextButton
        type="button"
        size="sm"
        className="wfm-text-editor__compose-cancel-btn"
        onMouseDown={composeExpandOnFocus ? handleComposeChromePointerDown : undefined}
        onClick={
          composeExpandOnFocus || autoMobileExpandOnFocus
            ? handleSecondaryActionClick
            : onSecondaryAction
        }
        disabled={secondaryActionDisabled}
      >
        {secondaryActionText || 'Cancel'}
      </NoOutlineTextButton>
    ) : null;

  const bottomBarToolbarControls = (
    <>
      {controlsVisibility.otherButtons ? mobileExpandToggle : null}
      {contentFlagGrouped}
      {controlsVisibility.toolbar && mode === 'rich' && (!composeExpandOnFocus || isComposeExpanded) ? (
        <CollapseTextButton
          type="button"
          shape="pill"
          color="gray"
          size="sm"
          variant="outline"
          className="wfm-text-editor__toolbar-visibility-btn"
          onMouseDown={composeExpandOnFocus ? handleComposeChromePointerDown : undefined}
          onClick={handleToolbarVisibilityToggle}
          iconStart={<CgToolbarTop aria-hidden="true" />}
        >
          {isToolbarVisible ? 'Hide Toolbar' : 'Show Toolbar'}
        </CollapseTextButton>
      ) : null}
    </>
  );

  const bottomBarLeft = primaryActionsOnRight ? (
    bottomBarToolbarControls
  ) : (
    <>
      {bottomBarPrimaryAction}
      {bottomBarSecondaryAction}
    </>
  );

  const bottomBarRight = primaryActionsOnRight ? (
    <>
      {bottomBarSecondaryAction}
      {bottomBarPrimaryAction}
    </>
  ) : (
    bottomBarToolbarControls
  );

  const hasBottomBarActions =
    Boolean(bottomBarPrimaryAction) ||
    Boolean(bottomBarSecondaryAction) ||
    Boolean(contentFlagGrouped) ||
    Boolean(controlsVisibility.otherButtons) ||
    (controlsVisibility.toolbar && mode === 'rich' && (!composeExpandOnFocus || isComposeExpanded));

  const composerActionBar =
    hasBottomBarActions ? (
      <div
        className={classNames(
          'wfm-text-editor__surface-bottom-bar',
          mode !== 'rich' && 'wfm-text-editor__surface-bottom-bar--markdown'
        )}
        role="group"
        aria-label="Composer actions"
      >
        <div className="wfm-text-editor__surface-bottom-actions-left">{bottomBarLeft}</div>
        <div className="wfm-text-editor__surface-bottom-actions-right">{bottomBarRight}</div>
      </div>
    ) : null;

  const editorShell = (
    <section
      ref={editorShellRef}
      className={classNames(
        'wfm-text-editor',
        isMobileExpanded && 'wfm-text-editor--mobile-expanded',
        isMobileExpanded && mode === 'rich' && !isToolbarVisible && 'wfm-text-editor--mobile-toolbar-collapsed',
        composeExpandOnFocus && !isComposeExpanded && 'wfm-text-editor--compose-collapsed',
        composeExpandOnFocus && isComposeExpanded && 'wfm-text-editor--compose-expanded',
        documentExport && mode === 'rich' && 'wfm-text-editor--document-page',
        useMobileReadableDocumentLayout && 'wfm-text-editor--document-page-mobile-readable',
        documentExport &&
          mode === 'rich' &&
          !resolvedPrintPageNumbers &&
          'wfm-text-editor--print-page-numbers-off',
        className
      )}
      style={editorShellStyle}
      onFocusOut={composeExpandOnFocus ? handleComposeBlur : undefined}
    >
      {showContentFlags && isSpoiler ? (
        <div className="wfm-text-editor__notice" role="alert">
          Spoiler warning: readers can skip this content if they do not want spoilers.
        </div>
      ) : null}

      {showContentFlags && isAgeRestricted ? (
        <div className="wfm-text-editor__notice wfm-text-editor__notice--danger" role="alert">
          18+ flag is enabled: comments should be hidden/disabled for under-18 viewers.
        </div>
      ) : null}

      {showContentFlags && isSensitivity ? (
        <div className="wfm-text-editor__notice wfm-text-editor__notice--sensitivity" role="alert">
          Sensitivity: let readers know this may cover emotionally intense or difficult themes.
        </div>
      ) : null}

      {mode === 'rich' ? (
        <div className="wfm-text-editor__rich-shell">
          <div
            ref={richComposerRef}
            className={classNames(
              'wfm-text-editor__rich-composer',
              !toolbarRevealed && 'wfm-text-editor__rich-composer--toolbar-collapsed',
              composeExpandOnFocus && isComposeExpanded && 'wfm-text-editor__rich-composer--compose-expanded'
            )}
          >
            {toolbarMounted ? (
              <div
                ref={richToolbarOverlayRef}
                className={classNames(
                  'wfm-text-editor__toolbar',
                  composeExpandOnFocus &&
                    !toolbarRevealed &&
                    'wfm-text-editor__toolbar--compose-reveal-pending',
                  composeExpandOnFocus && toolbarRevealed && 'wfm-text-editor__toolbar--compose-revealed'
                )}
                role="toolbar"
                aria-label="Formatting actions"
                aria-hidden={composeExpandOnFocus && !toolbarRevealed ? true : undefined}
                onMouseDown={handleToolbarComposePointerDown}
              >
                {canScrollToolbarLeft ? (
                  <button
                    type="button"
                    className="wfm-text-editor__toolbar-scroll-arrow wfm-text-editor__toolbar-scroll-arrow--left"
                    aria-label="Scroll toolbar left"
                    title="Scroll toolbar left"
                    onClick={() => shiftToolbar('left')}
                  >
                    <FaChevronLeft />
                  </button>
                ) : null}
                <div ref={toolbarScrollRef} className="wfm-text-editor__toolbar-scroll">
                  {fileMenuOptions.length ? (
                    <>
                      <TextEditorFileMenu options={fileMenuOptions} onAction={handleFileMenuAction} />
                      <span className="wfm-text-editor__tool-separator" aria-hidden="true" />
                    </>
                  ) : null}
                  <div
                    className="wfm-text-editor__typography"
                    role="group"
                    aria-label="Font and text color"
                    onMouseDownCapture={handleTypographyMouseDownCapture}
                  >
                    <div
                      className={classNames(
                        'wfm-text-editor__typography-dropdown',
                        'wfm-text-editor__typography-dropdown--family'
                      )}
                    >
                      <Dropdown
                        options={EDITOR_FONT_FAMILY_OPTIONS}
                        selectedOption={toolbarFontFamily}
                        onSelect={handleToolbarFontFamilySelect}
                        getSelectedLabel={editorFontFamilySelectedLabel}
                        searchable
                        searchPlaceholder="Search fonts…"
                        menuPortal
                        menuPortalZIndex={TEXT_EDITOR_MENU_PORTAL_Z_INDEX}
                        menuPortalMinWidth={200}
                        menuPortalMaxHeight={240}
                        menuPortalClassName="wfm-text-editor__typography-dropdown-menu"
                      />
                    </div>
                    <div className="wfm-text-editor__typography-size-combo">
                      <input
                        type="text"
                        inputMode="decimal"
                        className="wfm-text-editor__typography-size-input"
                        value={toolbarFontSizeDraft}
                        onChange={handleToolbarFontSizeInputChange}
                        onKeyDown={handleToolbarFontSizeInputKeyDown}
                        onBlur={handleToolbarFontSizeInputBlur}
                        aria-label="Font size (px or pt)"
                        title="Font size — type a value and press Enter, or pick from the list"
                        placeholder="Size"
                        autoComplete="off"
                        spellCheck={false}
                      />
                      <div
                        className={classNames(
                          'wfm-text-editor__typography-dropdown',
                          'wfm-text-editor__typography-dropdown--size',
                          'wfm-text-editor__typography-dropdown--size-picker'
                        )}
                      >
                        <Dropdown
                          options={EDITOR_FONT_SIZE_OPTIONS}
                          selectedOption={toolbarFontSize}
                          onSelect={handleToolbarFontSizeSelect}
                          getSelectedLabel={() => ''}
                          menuPortal
                          menuPortalZIndex={TEXT_EDITOR_MENU_PORTAL_Z_INDEX}
                          menuPortalMinWidth={88}
                          menuPortalMaxHeight={220}
                          menuPortalClassName="wfm-text-editor__typography-dropdown-menu"
                        />
                      </div>
                    </div>
                    <label
                      className="wfm-text-editor__color-control"
                      title="Text color"
                      style={{ '--wfm-editor-toolbar-color': toolbarTextColor }}
                    >
                      <span className="wfm-text-editor__color-control-icon" aria-hidden="true">
                        <MdFormatColorText />
                      </span>
                      <input
                        type="color"
                        className="wfm-text-editor__color-input"
                        value={toolbarTextColor}
                        onChange={handleToolbarTextColorChange}
                        aria-label="Text color"
                      />
                    </label>
                  </div>
                  <span className="wfm-text-editor__tool-separator" aria-hidden="true" />
                  {TOOLBAR_ITEMS.map((item) => {
                    const Icon = item.icon;
                    return (
                      <ButtonPill
                        key={item.key}
                        type="button"
                        shape="pill"
                        size="sm"
                        variant="outline"
                        className="wfm-text-editor__tool-btn"
                        title={item.label}
                        aria-label={item.label}
                        onClick={() => executeEditorCommand(item)}
                        iconStart={<Icon />}
                      >
                        {null}
                      </ButtonPill>
                    );
                  })}
                  {documentExport ? (
                    <>
                      <span className="wfm-text-editor__tool-separator" aria-hidden="true" />
                      <GroupedButton
                        triggerIcon={<TbFileOrientation aria-hidden />}
                        triggerText="Page"
                        align="left"
                        items={pagePropertiesMenuItems}
                        menuPortal
                        className="wfm-text-editor__page-properties-grouped"
                        menuClassName="wfm-text-editor__page-properties-menu"
                        ariaLabel="Page properties"
                        triggerTitle={pagePropertiesTriggerTitle}
                      />
                    </>
                  ) : null}
                </div>
                {canScrollToolbarRight ? (
                  <button
                    type="button"
                    className="wfm-text-editor__toolbar-scroll-arrow wfm-text-editor__toolbar-scroll-arrow--right"
                    aria-label="Scroll toolbar right"
                    title="Scroll toolbar right"
                    onClick={() => shiftToolbar('right')}
                  >
                    <FaChevronRight />
                  </button>
                ) : null}
                {toolbarMoreItems.length ? (
                  <div className="wfm-text-editor__toolbar-more">
                    <GroupedButton
                      triggerIcon={<IoIosMore />}
                      triggerText=""
                      align="right"
                      items={toolbarMoreItems}
                      menuPortal
                      menuClassName="wfm-text-editor__toolbar-more-menu"
                      className="wfm-text-editor__more-grouped"
                      ariaLabel="More toolbar actions"
                      onOpenChange={(open) => {
                        composeMoreMenuOpenRef.current = open;
                      }}
                    />
                  </div>
                ) : null}
              </div>
            ) : null}
            <div
              ref={surfaceWrapRef}
              className={classNames(
                'wfm-text-editor__surface-wrap',
                documentExport && `wfm-text-editor__surface-wrap--${pageOrientation}`,
                documentExport && `wfm-text-editor__surface-wrap--${pageLayoutMode}`,
                documentExport &&
                  mode === 'rich' &&
                  !useMobileReadableDocumentLayout &&
                  'wfm-text-editor__surface-wrap--scaled-preview',
                useMobileReadableDocumentLayout && 'wfm-text-editor__surface-wrap--mobile-readable'
              )}
            >
              <div
                className="wfm-text-editor__surface-page-scale-host"
                style={
                  documentExport && mode === 'rich' && !useMobileReadableDocumentLayout
                    ? {
                        width: pagePreviewHostWidthStyle,
                        maxWidth: '100%',
                        minHeight: pagePreviewHostMinHeightPx,
                        boxSizing: 'border-box',
                      }
                    : undefined
                }
              >
              <div
                className="wfm-text-editor__surface-page-canvas"
                style={
                  documentExport && mode === 'rich'
                    ? useMobileReadableDocumentLayout
                      ? {
                          width: '100%',
                          maxWidth: '100%',
                        }
                      : {
                          width: documentSheetWidthPx,
                          minHeight: documentPageCanvasLogicalHeight,
                          transform: `scale(${pagePreviewScale})`,
                        }
                    : isDocumentPaginatedLayout && documentCanvasMinHeight
                      ? { minHeight: documentCanvasMinHeight }
                      : undefined
                }
              >
                {isDocumentPaginatedLayout ? (
                  <DocumentPageGuides
                    pageCount={useMobileReadableDocumentLayout ? 1 : documentPageCount}
                    pageOrientation={pageOrientation}
                    showPageNumbers={resolvedPrintPageNumbers}
                    fullWidth={useMobileReadableDocumentLayout}
                  />
                ) : null}
              <div
                ref={editorRef}
                className={classNames(
                  'wfm-text-editor__surface',
                  'wfm-text-editor__surface--rich',
                  isRichContentEmpty && 'wfm-text-editor__surface--show-hidden-toolbar-placeholder',
                  isDocumentPaginatedLayout && 'wfm-text-editor__surface--paginated-pages'
                )}
                style={
                  isDocumentPaginatedLayout &&
                  documentCanvasMinHeight &&
                  !useMobileReadableDocumentLayout
                    ? { minHeight: documentCanvasMinHeight }
                    : undefined
                }
                data-placeholder={richPlaceholder}
                contentEditable
                suppressContentEditableWarning
                onInput={handleRichInput}
                onFocus={() => {
                  triggerAutoMobileExpand();
                  syncEditorThemeColors();
                  if (composeExpandOnFocus) {
                    expandCompose();
                  }
                }}
                onPointerDown={composeExpandOnFocus ? handleComposeTypingPointerDown : undefined}
                onBlur={() => {
                  applyHtmlAndEmit();
                }}
                onSelect={() =>
                  captureRichEditorSelection(editorRef.current, savedRichSelectionRef, lastNonEmptyRichSelectionRef)
                }
                onKeyUp={() =>
                  captureRichEditorSelection(editorRef.current, savedRichSelectionRef, lastNonEmptyRichSelectionRef)
                }
                onMouseUp={() =>
                  captureRichEditorSelection(editorRef.current, savedRichSelectionRef, lastNonEmptyRichSelectionRef)
                }
                onKeyDown={handleRichSurfaceKeyDown}
                onPointerDownCapture={handleEditorSurfacePointerDownCapture}
                aria-label="Rich text editor"
              />
              </div>
              </div>
            </div>
            {composerActionBar}
          </div>
        </div>
      ) : (
        <div
          ref={richComposerRef}
          className={classNames(
            'wfm-text-editor__markdown-composer',
            composeExpandOnFocus &&
              isComposeExpanded &&
              'wfm-text-editor__markdown-composer--compose-expanded'
          )}
        >
          <div className="wfm-text-editor__markdown-indicator" role="status" aria-live="polite">
            <div className="wfm-text-editor__markdown-indicator-left">
              <span className="wfm-text-editor__markdown-indicator-text">
                Markdown
                <FaMarkdown aria-hidden="true" />
              </span>
              <NoOutlineTextButton
                type="button"
                className="wfm-text-editor__help-link-btn"
                iconPrefix={<GoQuestion />}
                aria-label="Markdown help"
                title="Markdown help"
                onClick={() => setIsMarkdownHelpOpen(true)}
              />
            </div>
            <div className="wfm-text-editor__markdown-indicator-actions">
              {mobileExpandToggle}
              {primaryActionsOnRight ? null : contentFlagGrouped}
              {!lockEditorMode ? (
                <ButtonPill
                  type="button"
                  shape="pill"
                  size="sm"
                  variant="outline"
                  className="wfm-text-editor__rich-text-btn"
                  onClick={() => handleModeChange('rich')}
                >
                  Rich Text
                </ButtonPill>
              ) : null}
            </div>
          </div>
          <textarea
            ref={markdownTextareaRef}
            className="wfm-text-editor__markdown-input"
            rows={3}
            value={markdownContent}
            onChange={handleMarkdownChange}
            onKeyDown={handleMentionKeyDown}
            onFocus={triggerAutoMobileExpand}
            onPointerDown={composeExpandOnFocus ? handleComposeTypingPointerDown : undefined}
            placeholder={markdownPlaceholder}
            aria-label="Markdown editor"
          />
          {composerActionBar}
        </div>
      )}

      <CenterModal
        isOpen={modalState.isOpen}
        onClose={closeModal}
        width="fit-content"
        height="auto"
        overlayClassName="wfm-text-editor__modal-overlay"
        contentClassName="wfm-text-editor__modal wfm-text-editor__modal--fit"
      >
        <form className="wfm-text-editor__modal-form" onSubmit={handleModalSubmit}>
          <h3>{modalState.title}</h3>
          {modalState.type === 'insertImage' || modalState.type === 'insertVideo' ? (
            <div
              className={classNames('wfm-text-editor__upload-zone', isModalDragOver && 'is-drag-over')}
              onDragOver={(event) => {
                event.preventDefault();
                event.stopPropagation();
                setIsModalDragOver(true);
              }}
              onDragLeave={(event) => {
                event.preventDefault();
                event.stopPropagation();
                setIsModalDragOver(false);
              }}
              onDrop={handleModalDrop}
            >
              <p>Drag & drop {modalState.type === 'insertImage' ? 'an image' : 'a video'} here</p>
              <ButtonPill
                type="button"
                shape="pill"
                size="sm"
                variant="outline"
                className="wfm-text-editor__modal-secondary"
                onClick={() => modalFileInputRef.current?.click()}
              >
                Browse File
              </ButtonPill>
              <input
                ref={modalFileInputRef}
                type="file"
                accept={modalState.type === 'insertImage' ? 'image/*' : 'video/*'}
                onChange={handleModalFileInputChange}
                className="wfm-text-editor__upload-input"
              />
              {modalUploadFile ? <small>Selected: {modalUploadFile.name}</small> : null}
              {modalUploadError ? (
                <p className="wfm-text-editor__modal-upload-error" role="alert">
                  {modalUploadError}
                </p>
              ) : null}
            </div>
          ) : null}
          {modalState.fields.map((field) => (
            <label key={field.name} className="wfm-text-editor__modal-field">
              <span>{field.label}</span>
              {field.multiline ? (
                <textarea
                  value={modalValues[field.name] || ''}
                  onChange={(event) =>
                    setModalValues((current) => ({ ...current, [field.name]: event.target.value }))
                  }
                  placeholder={field.placeholder}
                  required={field.required}
                  rows={6}
                />
              ) : (
                <input
                  type="text"
                  value={modalValues[field.name] || ''}
                  onChange={(event) =>
                    setModalValues((current) => ({ ...current, [field.name]: event.target.value }))
                  }
                  placeholder={field.placeholder}
                  required={field.required}
                />
              )}
            </label>
          ))}
          <div className="wfm-text-editor__modal-actions">
            <ButtonPill
              type="submit"
              shape="pill"
              size="sm"
              className="wfm-text-editor__modal-primary"
              disabled={modalMediaBusy}
            >
              {modalMediaBusy ? 'Uploading…' : 'Apply'}
            </ButtonPill>
            <ButtonPill type="button" shape="pill" size="sm" variant="outline" className="wfm-text-editor__modal-secondary" onClick={closeModal}>
              Cancel
            </ButtonPill>
          </div>
        </form>
      </CenterModal>

      <CenterModal
        isOpen={isMarkdownHelpOpen}
        onClose={() => setIsMarkdownHelpOpen(false)}
        width="min(560px, 94vw)"
        height="auto"
        overlayClassName="wfm-text-editor__modal-overlay"
        contentClassName="wfm-text-editor__modal wfm-text-editor__markdown-help-modal"
      >
        <div className="wfm-text-editor__markdown-help">
          <h4>Markdown help with examples</h4>
          <ul>
            {markdownHelp.map((row) => (
              <li key={row.syntax}>
                <code>{row.syntax}</code> - <span>{row.example}</span>
              </li>
            ))}
          </ul>
        </div>
      </CenterModal>

      <CenterModal
        isOpen={isMentionMobileModalOpen}
        onClose={closeMentionMobileModal}
        width="calc(100vw - 20px)"
        height="auto"
        overlayClassName="wfm-mention-mobile-overlay"
        contentClassName="wfm-text-editor__mention-mobile-modal"
      >
        <div className="wfm-text-editor__mention-mobile-body">
          <h3 className="wfm-text-editor__mention-mobile-title">Mention someone</h3>
          <p className="wfm-text-editor__mention-mobile-hint">
            Type a name below (at least 2 characters). Choose someone from the list, then tap OK.
          </p>
          <label className="wfm-mention-mobile-search-label" htmlFor={mobileMentionSearchId}>
            Search people
          </label>
          <textarea
            ref={mobileMentionSearchRef}
            id={mobileMentionSearchId}
            className="wfm-mention-mobile-search"
            value={mobileMentionQuery}
            onChange={(e) => setMobileMentionQuery(e.target.value)}
            rows={2}
            inputMode="search"
            enterKeyHint="search"
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
            placeholder="e.g. first name (min. 2 characters)"
          />
          {mobileMentionListLoading ? (
            <p className="wfm-text-editor__mention-mobile-status">Searching…</p>
          ) : null}
          {!mobileMentionListLoading && String(mobileMentionQuery || '').trim().length < 2 ? (
            <p className="wfm-text-editor__mention-mobile-status wfm-text-editor__mention-mobile-status--muted">
              Type at least 2 characters to search the directory.
            </p>
          ) : null}
          {!mobileMentionListLoading && String(mobileMentionQuery || '').trim().length >= 2 && !mobileMentionList.length ? (
            <p className="wfm-text-editor__mention-mobile-status wfm-text-editor__mention-mobile-status--muted">No matches</p>
          ) : null}
          {mobileMentionList.length > 0 ? (
            <div className="wfm-text-editor__mention-mobile-list" role="listbox" aria-label="Mention search results">
              {mobileMentionList
                .filter((u) => String(u.email || '').trim())
                .map((u, idx) => {
                  const first =
                    String(u.name || '')
                      .trim()
                      .split(/\s+/)[0] || String(u.email || '').split('@')[0];
                  const em = String(u.email || '').trim();
                  const selected = em === String(selectedMentionEmail || '').trim();
                  return (
                    <button
                      key={`${em}-${u.userId || idx}`}
                      type="button"
                      role="option"
                      aria-selected={selected}
                      title={u.email || undefined}
                      className={classNames('wfm-text-editor__mention-row', 'wfm-text-editor__mention-row--modal', selected && 'is-active')}
                      onClick={() => setSelectedMentionEmail(em)}
                    >
                      <span className="wfm-text-editor__mention-name">@{first}</span>
                    </button>
                  );
                })}
            </div>
          ) : null}
          <div className="wfm-text-editor__mention-mobile-actions">
            <NoOutlineTextButton type="button" size="sm" onClick={closeMentionMobileModal}>
              Cancel
            </NoOutlineTextButton>
            <ButtonPill
              type="button"
              shape="pill"
              size="sm"
              color="brand"
              disabled={!selectedMentionEmail}
              onClick={applyMentionMobileChoice}
            >
              OK
            </ButtonPill>
          </div>
        </div>
      </CenterModal>

      {mention && mentionUserSearch && !isMentionMobileViewport
        ? createPortal(
            <div
              ref={mentionPopoverRef}
              className="wfm-text-editor__mention-popover"
              style={{
                position: 'fixed',
                top: mention.top,
                left: Math.min(
                  mention.left,
                  typeof window !== 'undefined' ? Math.max(8, window.innerWidth - 288) : mention.left
                ),
                zIndex: 10020,
              }}
              role="listbox"
              aria-label="Mention suggestions"
            >
              {mentionLoading ? (
                <div className="wfm-text-editor__mention-row wfm-text-editor__mention-row--muted">Searching…</div>
              ) : null}
              {!mentionLoading && String(mention.query || '').length < 2 ? (
                <div className="wfm-text-editor__mention-row wfm-text-editor__mention-row--muted">
                  Type at least 2 characters after @
                </div>
              ) : null}
              {!mentionLoading && String(mention.query || '').length >= 2 && !mentionRows.length ? (
                <div className="wfm-text-editor__mention-row wfm-text-editor__mention-row--muted">No matches</div>
              ) : null}
              {mentionRows.map((u, idx) => (
                <button
                  key={`${u.email}-${u.userId || idx}`}
                  type="button"
                  role="option"
                  aria-selected={idx === mentionHi}
                  title={u.email || undefined}
                  className={classNames('wfm-text-editor__mention-row', idx === mentionHi && 'is-active')}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    insertMentionChoice(u);
                  }}
                  onMouseEnter={() => setMentionHi(idx)}
                >
                  <span className="wfm-text-editor__mention-name">
                    @
                    {String(u.name || '')
                      .trim()
                      .split(/\s+/)[0] || String(u.email || '').split('@')[0]}
                  </span>
                </button>
              ))}
            </div>,
            document.body
          )
        : null}

      {mode === 'rich' && tableResizeUi
        ? createPortal(
            <div
              className="wfm-text-editor__table-props-floating"
              style={{
                position: 'fixed',
                zIndex: TABLE_EDITOR_PORTAL_Z,
                ...getTablePropsFloatingPosition(tableResizeUi),
              }}
            >
              <GroupedButton
                triggerIcon={<MdGridOn aria-hidden />}
                triggerText=""
                align="left"
                className="wfm-text-editor__table-structure-group"
                menuClassName="wfm-text-editor__table-structure-menu"
                ariaLabel="Table rows and columns"
                items={tableStructureGroupedItems}
              />
            </div>,
            document.body
          )
        : null}

      {mode === 'rich' && tableResizeUi
        ? createPortal(
            <div
              className="wfm-text-editor__table-resize-layer"
              style={{
                position: 'fixed',
                left: tableResizeUi.left,
                top: tableResizeUi.top,
                width: tableResizeUi.width,
                height: tableResizeUi.height,
                pointerEvents: 'none',
                zIndex: TABLE_EDITOR_PORTAL_Z - 1,
              }}
              aria-hidden
            >
              <button
                type="button"
                className="wfm-text-editor__table-resize-handle"
                title="Drag to resize table"
                aria-label="Drag to resize table"
                draggable={false}
                onDragStart={(ev) => ev.preventDefault()}
                onPointerDown={handleTableResizeHandlePointerDown}
              />
            </div>,
            document.body
          )
        : null}

      {documentExport && mode === 'rich' ? (
        <TextEditorPrintMarginsModal
          isOpen={isPrintMarginsModalOpen}
          onClose={() => setIsPrintMarginsModalOpen(false)}
          initialMarginsIn={customPrintMarginsIn}
          onApply={handlePrintMarginsCustomApply}
        />
      ) : null}
    </section>
  );

  /* Expanded mode must render under document.body so position:fixed is viewport-relative (ancestors often use transform). */
  if (isMobileExpanded && typeof document !== 'undefined') {
    return createPortal(
      <>
        <div
          className={classNames(
            'wfm-text-editor__mobile-expanded-backdrop',
            (autoMobileExpandOnFocus || expandedFromCreatePost) &&
              'wfm-text-editor__mobile-expanded-backdrop--story-compose'
          )}
          aria-hidden="true"
        />
        {editorShell}
      </>,
      document.body
    );
  }

  return editorShell;
}

